/**
 * TypeScript-modeller der spejler API-ets DTO-er (backend/TcPss.Api/Dtos).
 * Ændres en DTO i backenden, skal den tilsvarende type rettes her.
 */

export interface UserRef {
  id: number
  displayName: string
  email: string
  initials?: string | null
  photoUrl?: string | null
  /**
   * Arbejdsnummer fra Microsoft 365 (beslutning 041). Kun udfyldt de steder,
   * hvor nogen skal kunne ringe - f.eks. dagens kalender.
   */
  phoneNumber?: string | null
}

export interface User extends UserRef {
  isActive: boolean
  lastLoginAt?: string | null
  roles: string[]
  /** "entra" (Microsoft 365) eller "local" (adgangskode). */
  authProvider: 'entra' | 'local'
  mustChangePassword: boolean
  /** To-faktor er slået til. Altid false for Microsoft-brugere - de har det fra Entra. */
  twoFactorEnabled: boolean
  /** Rollen kræver to-faktor, så det kan ikke slås fra. */
  twoFactorRequired: boolean
  /**
   * Taget ud af systemet af en administrator (beslutning 036) - postkasser,
   * mødelokaler og servicekonti fra Microsoft 365. Kun sat for administratorer;
   * andre får slet ikke skjulte brugere at se.
   */
  isHidden: boolean
}

/**
 * Kom i gang (beslutning 080): hvor langt hver medarbejder er med at tage systemet i brug.
 * `never_logged_in` har aldrig været inde, `no_push` har været inde men får ingen beskeder
 * på telefonen, `ready` er klar.
 */
export type OnboardingStatus = 'never_logged_in' | 'no_push' | 'ready'

export interface OnboardingPerson {
  userId: number
  displayName: string
  email: string
  initials?: string | null
  photoUrl?: string | null
  phoneNumber?: string | null
  authProvider: string
  lastLoginAt?: string | null
  /** Har mindst én telefon eller browser slået beskeder til. */
  hasPush: boolean
  /** Hvornår der sidst blev sendt en invitation. Kommer fra aktivitetsloggen. */
  invitedAt?: string | null
  status: OnboardingStatus
  /** Hvorfor der ikke kan sendes til vedkommende - f.eks. manglende telefonnummer. */
  blocker?: string | null
  /** Præcis den SMS, vedkommende ville få. */
  message: string
  smsParts: number
}

export interface Onboarding {
  /** Er SMS sat op OG slået til? Ellers kan der ikke sendes invitationer. */
  smsReady: boolean
  /** Adressen der står i invitationen. Null hvis serveren ikke kender sin egen adresse. */
  link?: string | null
  neverLoggedIn: number
  withoutPush: number
  ready: number
  people: OnboardingPerson[]
}

export interface InviteResult {
  sent: number
  results: { userId: number; displayName: string; sent: boolean; reason?: string | null }[]
}

export interface AuthOptions {
  microsoftLoginEnabled: boolean
  localLoginEnabled: boolean
  /** "Glemt adgangskode?" - kun når lokalt login OG mailen virker (beslutning 078). */
  passwordResetEnabled?: boolean
}

/**
 * Svaret på et loginforsøg. Enten er brugeren inde (token er sat), eller der
 * mangler et trin - se twoFactorRequired / twoFactorSetupRequired (beslutning 035).
 */
export interface LoginResponse {
  twoFactorRequired: boolean
  twoFactorSetupRequired: boolean
  token: string | null
  expiresAt: string | null
  user: User | null
  /** Kortlivet billet der beviser, at adgangskoden var rigtig. */
  challenge: string | null
  challengeExpiresAt: string | null
  /** Kun sat lige efter en tilmelding. Kan aldrig hentes igen. */
  recoveryCodes: string[] | null
}

export interface TwoFactorSetup {
  /** Base32-hemmeligheden til manuel indtastning i appen. */
  secret: string
  /** otpauth-URI'en der tegnes som QR-kode. */
  uri: string
}

export interface TwoFactorStatus {
  enabled: boolean
  required: boolean
  /** Der er lavet en hemmelighed, men den er aldrig bekræftet. */
  setupPending: boolean
  recoveryCodesLeft: number
}

export interface RecoveryCodes {
  recoveryCodes: string[]
}

/** En medarbejder der kan modtage en besked (beslutning 042). */
export interface MessageRecipient {
  id: number
  displayName: string
  email: string
  initials?: string | null
  photoUrl?: string | null
  /** Null betyder, at vedkommende ikke kan få en SMS. */
  phoneNumber?: string | null
}

export interface MessageKind {
  type: string
  name: string
  description: string
  sms: boolean
  mail: boolean
  /** Sendes med det samme - også uden for åbningstiden (beslutning 138). Kun Superadmin sætter det. */
  important: boolean
}

/** Én ugedag - tom åbner/lukker betyder lukket (beslutning 138). */
export interface OfficeDay {
  open?: string | null
  close?: string | null
}

export interface OfficeHours {
  enabled: boolean
  /** Mandag ... søndag. */
  days: OfficeDay[]
}

export interface MessageSettings {
  /** Er SMS både sat op og slået til? Ellers gør fluebenene ingenting. */
  smsReady: boolean
  mailReady: boolean
  smsSenderName?: string | null
  kinds: MessageKind[]
  /** Åbningstiden for SMS og mail (beslutning 138). */
  officeHours?: OfficeHours | null
  isOpenNow: boolean
  /** Næste åbning i dansk tid. */
  nextOpening?: string | null
  canEditOfficeHours: boolean
  /** SMS/mail, der venter i kø. */
  queuedCount: number
}

export interface MessageResult {
  userId: number
  displayName: string
  sms: boolean
  mail: boolean
  /** Gik det galt, står forklaringen her. */
  reason?: string | null
}

export interface SendMessageResponse {
  reached: number
  results: MessageResult[]
}

/** Opsætningen af udsendelse af mail (beslutning 038). */
export interface MailSettings {
  enabled: boolean
  /** Afsenderpostkassen. Skal være en rigtig postkasse i Microsoft 365. */
  fromAddress: string | null
  replyTo: string | null
  isConfigured: boolean
  /** Er Microsoft 365 sat op med app-only adgang? Uden den kan der ikke sendes. */
  graphReady: boolean
}

/** Opsætningen af SMS-gatewayen (beslutning 037). API-nøglen udleveres aldrig. */
export interface SmsSettings {
  enabled: boolean
  baseUrl: string | null
  /** Er der gemt en nøgle? Selve nøglen kommer aldrig ud af serveren. */
  hasApiKey: boolean
  referencePrefix: string
  /** Er der både adresse og nøgle? */
  isConfigured: boolean
  /** Navnet der skrives forrest i hver SMS. Tom = standarden "Flow-TC". */
  senderName?: string | null
}

export interface SmsSendResult {
  success: boolean
  messageId: string | null
  status: string | null
  errorCode: string | null
  error: string | null
}

/** Hvor meget en besked fylder, talt som mobilnettet gør. */
export interface SmsSize {
  /** "GSM-7" eller "UCS-2". Ét emoji slår hele beskeden om til UCS-2. */
  encoding: string
  characters: number
  parts: number
  remaining: number
}

/** En bruger der ikke blev slettet, og hvorfor (beslutning 039). */
export interface SkippedUser {
  id: number
  displayName: string
  reason: string
}

export interface DeleteUsersResponse {
  deleted: number
  deletedNames: string[]
  skipped: SkippedUser[]
}

/** Svar på at skjule eller genindsætte brugere (beslutning 036). */
export interface HideUsersResponse {
  changed: number
  /** Brugere der blev sprunget over, med en forklaring til hver. */
  skipped: string[]
}

export interface Lookup {
  id: number
  name: string
  systemCode?: string | null
  color: string
  icon?: string | null
  isActive: boolean
  sortOrder: number
  isCompleted: boolean
  isWaiting: boolean
  isCancelled: boolean
  weight: number
}

export interface Paged<T> {
  items: T[]
  total: number
  page: number
  pageSize: number
}

export interface Project {
  id: number
  name: string
  /** Kundens navn (fra kunderegisteret). */
  customer?: string | null
  customerId?: number | null
  category?: string | null
  /** "project" = almindelig sag, "service" = servicesag hos en kunde (beslutning 076). */
  kind: 'project' | 'service'
  /** Ordrenummeret i Ordrestyring (beslutning 077). Null på sager fra før feltet. */
  orderNumber?: string | null
  /** Kundens eget sagsnummer - det, kunden kører med (beslutning 083). */
  customerCaseNumber?: string | null
  /**
   * Er sagsnummeret dannet af vores ordrenummer frem for oplyst af kunden?
   * Tallet afslører det ikke selv, så det skal stå her.
   */
  customerCaseNumberIsGenerated?: boolean
  /** Sagens eget arbejdssted (beslutning 044). Null = arbejdet foregår hos kunden. */
  siteAddress?: string | null
  sitePostalCode?: string | null
  siteCity?: string | null
  projectManager?: UserRef | null
  status: Lookup
  priority: Lookup
  progressPercent: number
  progressIsManual: boolean
  startDate?: string | null
  deadline?: string | null
  nextActionText?: string | null
  nextActionUser?: UserRef | null
  nextActionDeadline?: string | null
  waitingReason?: string | null
  waitingSince?: string | null
  isArchived: boolean
  taskCount: number
  completedTaskCount: number
  overdueTaskCount: number
  teamName?: string | null
  updatedAt: string
}

export interface Task {
  id: number
  projectId: number
  projectName: string
  title: string
  description?: string | null
  assignee?: UserRef | null
  coAssignees: UserRef[]
  status: Lookup
  priority: Lookup
  startDate?: string | null
  deadline?: string | null
  completedAt?: string | null
  waitingReason?: string | null
  waitingSince?: string | null
  weight: number
  sortOrder: number
  commentCount: number
  isOverdue: boolean
  createdAt: string
  updatedAt: string
  /**
   * Maa den indloggede saette en medarbejder paa opgaven? Serveren svarer, saa
   * brugerfladen ikke gentager adgangsreglen og kan komme til at vise en knap,
   * API-et bagefter afviser (beslutning 065).
   */
  canAssign: boolean
  /** Hvor langt opgaven er, 0-100 (beslutning 085). En færdig opgave tæller altid 100. */
  progressPercent: number
  /** Hovedemnet fra procedureskabelonen (beslutning 141). Null = opgaven står for sig. */
  section?: string | null
  /** Teamet, opgaven ligger hos, med alle medlemmer (beslutning 142). */
  team?: { id: number; name: string; members: UserRef[] } | null
  /** Antal indskrivninger i loggen over udført arbejde. */
  workLogCount: number
  /** Punkter på mangellisten, der ikke er klar - både det, der mangler, og det bestilte. */
  openMissingCount: number
  /** Heraf bestilt og undervejs. */
  orderedCount: number
}

/** Én indskrivning i loggen over udført arbejde (beslutning 085). */
export interface TaskWorkLog {
  id: number
  user: UserRef
  body: string
  createdAt: string
}

export type TaskMissingItemStatus = 'missing' | 'ordered' | 'ready'

/** Et punkt på opgavens mangelliste (beslutning 085). */
/** Et trin på en note til næste gang: Bestilt, Pakket i sagskassen eller Klaret (uden trin). */
export type ProjectNeedStep = 'ordered' | 'packed' | 'done'

/**
 * En note øverst på sagen om, hvad der mangler til næste gang (beslutning 143).
 * Ikke opgavens mangelliste (`TaskMissingItem`) og ikke afleveringens mangler (`Defect`).
 */
export interface ProjectNeed {
  id: number
  text: string
  needsOrder: boolean
  needsPacking: boolean
  orderedAt?: string | null
  orderedBy?: UserRef | null
  packedAt?: string | null
  packedBy?: UserRef | null
  doneAt?: string | null
  doneBy?: UserRef | null
  createdBy?: UserRef | null
  createdAt: string
  /** Alle trin, noten er markeret med, er gjort. */
  isDone: boolean
  canDelete: boolean
}

/** En sag, der er klar til faktura (beslutning 147). Adressen er sagens egen, ellers kundens. */
export interface InvoiceReadyProject {
  id: number
  name: string
  orderNumber?: string | null
  kind: string
  customer?: string | null
  address?: string | null
  postalCode?: string | null
  city?: string | null
  projectManager?: UserRef | null
  taskCount: number
  readySince: string
}

/** Hvor sagskassen er (beslutning 144). */
export type CaseBoxLocation = 'vehicle' | 'office'

export interface ProjectBoxMove {
  id: number
  location: CaseBoxLocation
  user?: UserRef | null
  /** "AB 12 345 (Transit)" - kun når kassen kom i en firmabil. */
  vehicleName?: string | null
  createdAt: string
}

/** En sagskasse (beslutning 145). En sag kan have flere; nummeret står i QR-koden. */
export interface ProjectBox {
  number: number
  /** Valgfrit, f.eks. "Kabler". */
  name?: string | null
  /** Null, før kassen er flyttet første gang. */
  current?: ProjectBoxMove | null
  history: ProjectBoxMove[]
  canDelete: boolean
}

export interface TaskMissingItem {
  id: number
  text: string
  status: TaskMissingItemStatus
  /** Mangler, Bestilt eller Klar. */
  statusName: string
  /** Hvad der er bestilt og hos hvem. */
  orderNote?: string | null
  /** yyyy-MM-dd */
  expectedDelivery?: string | null
  updatedBy?: UserRef | null
  createdAt: string
  updatedAt: string
  readyAt?: string | null
}

export interface Comment {
  id: number
  user: UserRef
  body: string
  mentions: UserRef[]
  createdAt: string
  editedAt?: string | null
}

export interface ProjectDetail {
  project: Project
  description?: string | null
  members: UserRef[]
  tasks: Task[]
  comments: Comment[]
  createdAt: string
  completedAt?: string | null
  archivedAt?: string | null
  site?: SiteContact | null
  /** Sagens eget arbejdssted. Null = arbejdet foregår på kundens adresse. */
  workSite?: ProjectSite | null
  /**
   * Skabelonen sagen blev oprettet fra. KUN en reference tilbage (beslutning
   * 021) - opgaverne er kopieret ind. Bruges til at finde de manualer, der
   * hører til sagstypen (beslutning 063).
   */
  createdFromTemplateId?: number | null
  /** Rettigheder i ampere der, hvor arbejdet foregår (beslutning 109). */
  connectionAmps?: number | null
  /** True = tallet står på sagens egen adresse, false = på kunden. */
  connectionAmpsOnSite?: boolean
}

/** Stedet arbejdet udføres, når det ikke er kundens egen adresse. */
export interface ProjectSite {
  address?: string | null
  postalCode?: string | null
  city?: string | null
  latitude?: number | null
  longitude?: number | null
  /** "ok", "not_found", "ambiguous", "error", "no_address". Andet end ok = ikke på kortet. */
  geocodeStatus?: string | null
  /** Den adresse, opslaget ramte. */
  matchedAddress?: string | null
}

export interface ActivityEntry {
  id: number
  actorName: string
  action: string
  entityType: string
  entityId?: number | null
  entityName?: string | null
  field?: string | null
  oldValue?: string | null
  newValue?: string | null
  summary?: string | null
  projectId?: number | null
  createdAt: string
}

export interface AppNotification {
  id: number
  type: string
  title: string
  body?: string | null
  link?: string | null
  isRead: boolean
  createdAt: string
}

export interface MyWork {
  myProjects: Project[]
  myTasks: Task[]
  waitingOnMe: Task[]
  dueSoon: Task[]
  overdue: Task[]
}

export interface DashboardStats {
  activeProjects: number
  waitingProjects: number
  overdueProjects: number
  completedThisMonth: number
  activeTasks: number
  tasksDueThisWeek: number
  overdueTasks: number
}

export interface DashboardView {
  dashboardId: number
  dashboardName: string
  layout: string
  screenName?: string | null
  generatedAt: string
  refreshIntervalSeconds: number
  statistics: DashboardStats
  projects: Project[]
  recentActivity: ActivityEntry[]
  /** Dagens bemanding, når dashboardet viser den (beslutning 076). */
  staffing?: DaySchedule | null
  /** Dagens fødselsdage og jubilæer - "Tillykke" nederst på skærmen (beslutning 117). */
  celebrations?: Celebration[] | null
  /** Tilkaldevagten nu og næste gang (beslutning 120). Null = funktionen er slået fra. */
  onCall?: OnCallNow | null
  /** Ugens faglige tip (beslutning 121) - kun teksten. */
  tip?: { title: string; body: string } | null
  /** "Ugen der gik" fredag fra kl. 14 (beslutning 126) - aldrig hvem der har ferie. */
  weekSummary?: WeekSummaryTv | null
  /** Kortet over dashboardets åbne sager, når det vises i stedet for tabellen (beslutning 111). */
  map?: MapData | null
}

/** En begivenhed eller et måltid næste uge i "Ugen der gik" (beslutning 126). */
export interface WeekSummaryItem {
  title: string
  at: string
  allDay: boolean
  location?: string | null
}

/** Det, TV'et viser af "Ugen der gik" (beslutning 126). */
export interface WeekSummaryTv {
  weekNumber: number
  projectsCompleted: number
  serviceProjectsCompleted: number
  serviceVisitsCompleted: number
  projectsCreated: number
  nextOnCall?: string | null
  nextWeek: WeekSummaryItem[]
}

export interface DashboardFilter {
  id: number
  filterType: string
  value: string
  include: boolean
}

export interface Dashboard {
  id: number
  name: string
  description?: string | null
  layout: string
  refreshIntervalSeconds: number
  showStatistics: boolean
  showRecentActivity: boolean
  /** Vis dagens bemanding på skærmen (beslutning 076). */
  showStaffing: boolean
  /** Vis sagerne på et kort i stedet for tabellen (beslutning 111). */
  showMap: boolean
  /** Hvem dagens bemanding viser. Tom = alle (beslutning 076). */
  staffingUserIds: number[]
  maxProjects: number
  isDefault: boolean
  filters: DashboardFilter[]
  screenCount: number
}

export interface ScreenSchedule {
  dayOfWeek: number
  onTime: string
  offTime: string
  isActive: boolean
}

export interface Screen {
  id: number
  name: string
  deviceId: string
  location?: string | null
  dashboardId?: number | null
  dashboardName?: string | null
  isActive: boolean
  isOnline: boolean
  lastSeenAt?: string | null
  lastIpAddress?: string | null
  clientVersion?: string | null
  tvPowerState?: string | null
  scheduleOverrideState?: string | null
  scheduleOverrideUntil?: string | null
  activeTokenCount: number
  schedules: ScreenSchedule[]
  /** Er skaermen bagud i forhold til den TV-klient, serveren har klar? (beslutning 074) */
  updateAvailable: boolean
}

export interface ScreenCreated {
  screen: Screen
  deviceToken: string
}

export interface Team {
  id: number
  name: string
  description?: string | null
  isActive: boolean
  memberCount: number
  /**
   * Sager der er sat paa teamet. Bruges til at sige konkret, hvad der sker, hvis
   * teamet fjernes - sagerne bliver staaende, men mister deres team.
   */
  projectCount: number
  /** Holdet - deres profilbilleder vises ved teamet (beslutning 142). */
  members: UserRef[]
}

export interface TeamInput {
  name: string
  description?: string | null
  isActive: boolean
  /** Hele holdet. Listen ERSTATTER medlemmerne - den er svaret paa "hvem er med". */
  memberIds: number[]
}

export interface SearchHit {
  type: string
  id: number
  title: string
  subtitle?: string | null
  link?: string | null
}

export interface SearchResults {
  projects: SearchHit[]
  tasks: SearchHit[]
  users: SearchHit[]
  comments: SearchHit[]
  /** Kunder og enheder, også på adresse (beslutning 110). */
  customers?: SearchHit[] | null
  devices?: SearchHit[] | null
}

/** Rollenavne som de står i databasen. */
export const Roles = {
  Superadmin: 'Superadmin',
  Administrator: 'Administrator',
  Office: 'Kontor',
  ProjectManager: 'Projektleder',
  Employee: 'Medarbejder',
  Apprentice: 'Lærling'
} as const

// --- Kalender / planlægning ---

export interface WorkAssignment {
  id: number
  user: UserRef
  /** Null når posten er et servicebesøg. */
  projectId?: number | null
  projectName: string
  projectCustomer?: string | null
  projectStatusColor: string
  taskId?: number | null
  taskTitle?: string | null
  /** yyyy-MM-dd */
  date: string
  startTime?: string | null
  endTime?: string | null
  note?: string | null
  site?: SiteContact | null
  /** Sat når posten er et planlagt servicebesøg. */
  deviceId?: number | null
  deviceName?: string | null
  deviceCode?: string | null
  /** Sagens ordrenummer, fx "4850-1". Null på servicebesøg og ældre sager. */
  projectOrderNumber?: string | null
}

export interface ScheduleTask {
  taskId: number
  title: string
  projectId: number
  projectName: string
  statusName: string
  statusColor: string
  deadline?: string | null
  isOverdue: boolean
  isDueToday: boolean
}

/**
 * Sagens næste handling, som den vises i dagsoversigten (beslutning 040).
 * Hverken en opgave eller en planlagt dag - den ene ting der skal ske på sagen nu.
 */
export interface ScheduleNextAction {
  projectId: number
  projectName: string
  projectCustomer?: string | null
  statusColor: string
  text: string
  deadline: string
  isOverdue: boolean
  isDueToday: boolean
}

export interface ScheduleRow {
  user: UserRef
  assignments: WorkAssignment[]
  tasks: ScheduleTask[]
  nextActions: ScheduleNextAction[]
  /**
   * Fravær der dækker dagen (beslutning 053). Tom eller udeladt, når manden er
   * på arbejde - eller når fraværsmodulet er slået fra. Posterne står der stadig:
   * en mand kan være kaldt ind fra ferie.
   */
  absences?: ScheduleAbsence[]
}

/** Fravær som det vises på en række i kalenderen. */
export interface ScheduleAbsence {
  id: number
  type: string
  typeName: string
  color: string
  startDate: string
  endDate: string
  note?: string | null
}

/** En fraværsperiode, som den står på fraværslisten. */
export interface Absence {
  id: number
  user: UserRef
  type: string
  typeName: string
  color: string
  startDate: string
  endDate: string
  dayCount: number
  note?: string | null
  createdBy?: UserRef | null
}

export interface AbsenceType {
  code: string
  name: string
  color: string
}

/** Et certifikat eller kursusbevis (beslutning 054). */
export interface Certificate {
  id: number
  user: UserRef
  name: string
  issuer?: string | null
  certificateNumber?: string | null
  issuedOn?: string | null
  /** Tom = beviset udløber ikke, og der varsles aldrig om det. */
  expiresOn?: string | null
  /** Negativt = udløbet for så mange dage siden. Null når det ikke udløber. */
  daysUntilExpiry?: number | null
  isExpired: boolean
  notes?: string | null
  isActive: boolean
  createdBy?: UserRef | null
}

export interface CertificateSummary {
  expired: number
  dueSoon: number
  warningDays: number
}

export interface CertificateInput {
  userId: number
  name: string
  issuer?: string | null
  certificateNumber?: string | null
  issuedOn?: string | null
  expiresOn?: string | null
  notes?: string | null
  isActive: boolean
}

/** En nøgle, brik eller kode (beslutning 055). */
export interface AccessKey {
  id: number
  name: string
  keyType: string
  keyTypeName: string
  customerId?: number | null
  customerName?: string | null
  identifier?: string | null
  location?: string | null
  storedAt?: string | null
  notes?: string | null
  isActive: boolean
  /** Sat når nøglen er ude hos nogen. Null = den ligger, hvor den skal. */
  currentLoan?: AccessKeyLoan | null
}

export interface AccessKeyLoan {
  id: number
  user: UserRef
  handedOutOn: string
  returnedOn?: string | null
  /** Tælles til i dag, hvis nøglen stadig er ude. */
  daysOut: number
  note?: string | null
  handedOutBy?: UserRef | null
}

export interface AccessKeySummary {
  total: number
  out: number
  longOut: number
  longOutDays: number
}

export interface AccessKeyType {
  code: string
  name: string
}

export interface AccessKeyInput {
  name: string
  keyType: string
  customerId?: number | null
  identifier?: string | null
  location?: string | null
  storedAt?: string | null
  notes?: string | null
  isActive: boolean
}

/** Hvad afleveringsmappen kommer til at indeholde (beslutning 056). */
export interface HandoverPreview {
  tasks: number
  photos: number
  documents: number
  /** Hvor mange fotos der faktisk kommer med - resten nævnes i mappen. */
  photosIncluded: number
  maxPhotos: number
  filesModuleEnabled: boolean
  /** Aabne punkter paa fejl- og mangellisten (beslutning 058). */
  openDefects: number
  defectsModuleEnabled: boolean
}

/** Tilstanden paa et stykke vaerktoej (beslutning 096). */
export type EquipmentStatus = 'ok' | 'service' | 'defect'

/** En kategori af vaerktoej - hvad slags vaerktoej det er (beslutning 096). */
export interface EquipmentCategory {
  id: number
  name: string
  icon?: string | null
  sortOrder: number
  isActive: boolean
  /** Hvor meget grej der staar i kategorien. En kategori i brug kan ikke slettes. */
  equipmentCount: number
}

export interface EquipmentCategoryInput {
  name: string
  icon?: string | null
  sortOrder: number
  isActive: boolean
}

/** En reservation af vaerktoej i en periode (beslutning 096). */
export interface EquipmentReservation {
  id: number
  user: UserRef
  fromDate: string
  toDate: string
  days: number
  note?: string | null
  projectId?: number | null
  projectName?: string | null
  /** Er det mig, der har reserveret? Saa maa jeg selv aflyse. */
  isMine: boolean
  /** Gaelder reservationen i dag? */
  isActiveNow: boolean
  cancelledAt?: string | null
  fulfilledAt?: string | null
}

/** Et stykke materiel paa udlaan (beslutning 057). */
export interface Equipment {
  id: number
  name: string
  equipmentType?: string | null
  /** Kategorien (beslutning 096). Tom = uden kategori. */
  categoryId?: number | null
  categoryName?: string | null
  status: EquipmentStatus
  /** Tilstanden paa dansk - serveren skriver teksten, saa de to ikke siger forskelligt. */
  statusLabel: string
  /** Kan grejet tages med eller reserveres - set paa tilstanden alene? */
  isUsable: boolean
  /** Koden paa QR-maerkatet. */
  code: string
  /** Stien til fotoet med versionsnummer. Tom = intet foto. */
  photoUrl?: string | null
  identifier?: string | null
  manufacturer?: string | null
  serialNumber?: string | null
  storedAt?: string | null
  /** Tom = grejet har ikke et fast eftersyn, og der varsles aldrig. */
  inspectionIntervalMonths?: number | null
  lastInspectionOn?: string | null
  nextInspectionOn?: string | null
  /** Negativt = eftersynet er overskredet. Null naar der ikke efterses. */
  daysUntilInspection?: number | null
  isInspectionOverdue: boolean
  notes?: string | null
  isActive: boolean
  /** Sat naar grejet er ude. Null = det ligger, hvor det skal. */
  currentLoan?: EquipmentLoan | null
  /** Den foerstkommende aabne reservation. Null = ingen staar i koe. */
  nextReservation?: EquipmentReservation | null
  openReservations: number
}

export interface EquipmentLoan {
  id: number
  user: UserRef
  handedOutOn: string
  returnedOn?: string | null
  daysOut: number
  /** Hvor mange dage man regnede med at skulle bruge det. Null = intet skoen. */
  expectedDays?: number | null
  expectedReturnOn?: string | null
  /** Er den forventede dag passeret? Et skoen, ikke en forseelse. */
  isOverdue: boolean
  note?: string | null
  projectId?: number | null
  projectName?: string | null
  handedOutBy?: UserRef | null
}

/** Et billede paa en post i vaerktoejets log (beslutning 096). */
export interface EquipmentLogPhoto {
  id: number
  /** Stien billedet hentes fra. Ligger bag login som alle andre fotos. */
  url: string
  caption?: string | null
}

/**
 * En post i vaerktoejets log: "service" (hvad blev lavet og skiftet - alle maa
 * skrive) eller "inspection" (eftersynet, der rykker naeste dato - kun
 * administratorer). Beslutning 096.
 */
export interface EquipmentInspection {
  id: number
  kind: 'service' | 'inspection'
  performedOn: string
  performedBy?: string | null
  isApproved: boolean
  workPerformed?: string | null
  partsReplaced?: string | null
  notes?: string | null
  registeredBy?: UserRef | null
  photos: EquipmentLogPhoto[]
}

export interface EquipmentSummary {
  total: number
  out: number
  inspectionOverdue: number
  inspectionDue: number
  warningDays: number
  /** Grej der er til service eller defekt. */
  needsAttention: number
  /** Grej der er reserveret af nogen i dag. */
  reservedToday: number
}

export interface EquipmentInput {
  name: string
  equipmentType?: string | null
  categoryId?: number | null
  status?: EquipmentStatus | null
  identifier?: string | null
  manufacturer?: string | null
  serialNumber?: string | null
  storedAt?: string | null
  inspectionIntervalMonths?: number | null
  lastInspectionOn?: string | null
  nextInspectionOn?: string | null
  notes?: string | null
  isActive: boolean
}

/** Et punkt paa fejl- og mangellisten (beslutning 058). */
export interface Defect {
  id: number
  /** Nummeret paa listen, saa man kan tale om "punkt 7" i telefonen. */
  number: number
  title: string
  location?: string | null
  description?: string | null
  status: string
  statusName: string
  color: string
  assignedUser?: UserRef | null
  deadline?: string | null
  /** Aaben og over fristen. En lukket mangel er aldrig for sen. */
  isOverdue: boolean
  raisedBy?: string | null
  raisedOn: string
  fixedOn?: string | null
  fixedBy?: UserRef | null
  rejectionReason?: string | null
  photoFileId?: number | null
  fixedPhotoFileId?: number | null
}

export interface DefectList {
  defects: Defect[]
  open: number
  fixed: number
  rejected: number
  overdue: number
}

export interface DefectInput {
  title: string
  location?: string | null
  description?: string | null
  assignedUserId?: number | null
  deadline?: string | null
  raisedBy?: string | null
  raisedOn?: string | null
  photoFileId?: number | null
}

/** En garanti paa noget installeret hos en kunde (beslutning 059). */
export interface Warranty {
  id: number
  name: string
  warrantyType: string
  warrantyTypeName: string
  /** Hvem der haefter - den korte forklaring, naar kunden ringer. */
  liable: string
  color: string
  customerId: number
  customerName: string
  projectId?: number | null
  projectName?: string | null
  deviceId?: number | null
  deviceName?: string | null
  supplier?: string | null
  reference?: string | null
  startsOn: string
  expiresOn: string
  /** Negativt = udloebet for saa mange dage siden. */
  daysUntilExpiry: number
  isExpired: boolean
  notes?: string | null
  isActive: boolean
  openClaims: number
  totalClaims: number
}

export interface WarrantyClaim {
  id: number
  title: string
  description?: string | null
  reportedOn: string
  status: string
  statusName: string
  color: string
  outcome?: string | null
  resolvedOn?: string | null
  createdBy?: UserRef | null
}

export interface WarrantySummary {
  total: number
  active: number
  expiringSoon: number
  expired: number
  openClaims: number
  warningDays: number
}

export interface WarrantyType {
  code: string
  name: string
  liable: string
  color: string
}

export interface WarrantyInput {
  name: string
  warrantyType: string
  customerId: number
  projectId?: number | null
  deviceId?: number | null
  supplier?: string | null
  reference?: string | null
  startsOn: string
  months?: number | null
  expiresOn?: string | null
  notes?: string | null
  isActive: boolean
}

/** En besked eller et akut udkald til én mand i marken (beslutning 060). */
export interface FieldMessage {
  id: number
  kind: string
  kindName: string
  kindColor: string
  isUrgent: boolean
  title: string
  description?: string | null
  status: string
  statusName: string
  statusColor: string
  isOpen: boolean
  recipient: UserRef
  sentBy?: UserRef | null
  projectId?: number | null
  projectName?: string | null
  customerId?: number | null
  customerName?: string | null
  deviceId?: number | null
  deviceName?: string | null
  /** Samme form som kalenderen bruger, saa tryk-for-at-ringe og kort virker. */
  site?: SiteContact | null
  createdAt: string
  acknowledgedAt?: string | null
  /** Minutter fra sendt til set. Null indtil han kvitterer. */
  minutesToAcknowledge?: number | null
  onWayAt?: string | null
  closedAt?: string | null
  minutesOpen: number
  outcome?: string | null
}

export interface FieldMessageSend {
  kind: string
  title: string
  description?: string | null
  recipientUserIds: number[]
  projectId?: number | null
  customerId?: number | null
  deviceId?: number | null
  address?: string | null
  postalCode?: string | null
  city?: string | null
  contactName?: string | null
  contactPhone?: string | null
}

export interface AbsenceInput {
  userId: number
  type: string
  startDate: string
  endDate: string
  note?: string | null
}

export interface DaySchedule {
  date: string
  rows: ScheduleRow[]
}

export interface WorkAssignmentInput {
  userId: number
  /** Enten denne eller `deviceId` - en post er en sag eller et servicebesøg. */
  projectId?: number | null
  /** Enheden der skal serviceres (beslutning 028). */
  deviceId?: number | null
  taskId?: number | null
  date: string
  startTime?: string | null
  endTime?: string | null
  note?: string | null
  additionalDates?: string[]
}

export interface CopyWeekInput {
  /** En dato i den uge, der kopieres FRA. Normaliseres til mandag på serveren. */
  fromWeek: string
  /** En dato i den uge, der kopieres TIL. */
  toWeek: string
  /** Kun denne medarbejder. Udeladt = alle. */
  userId?: number | null
}

export interface CopyWeekResult {
  fromWeek: string
  toWeek: string
  copied: number
  /** Poster der allerede stod i målugen. */
  skippedDuplicates: number
  /** Poster på arkiverede sager. */
  skippedArchived: number
  assignments: WorkAssignment[]
}

// --- Kort ---

/**
 * Et punkt på kortet: en åben sag eller et forfaldent serviceeftersyn - eller, på kortet over kunder og
 * enheder, en kunde eller en enhed med sin egen adresse (beslutning 109).
 */
export interface MapPoint {
  kind: 'project' | 'service' | 'customer' | 'device'
  id: number
  name: string
  customerName?: string | null
  customerId?: number | null
  address?: string | null
  latitude: number
  longitude: number
  statusName: string
  statusColor: string
  /** yyyy-MM-dd */
  dueDate?: string | null
  isOverdue: boolean
  assignedUserNames: string[]
  /** Den adresse, punktet faktisk blev sat på. */
  matchedAddress?: string | null
  /** Sagen har sin egen adresse - nålen står på arbejdsstedet, ikke hos kunden. */
  isOwnSite?: boolean
}

export interface MapData {
  points: MapPoint[]
  /** Sager/enheder hvis kunde slet ingen adresse har. */
  missingAddress: number
  /**
   * Adressen ER slået op og kunne ikke bestemmes. **Det er de eneste, nogen
   * skal gøre noget ved.**
   */
  missingPosition: number
  /**
   * Adressen er sat, men endnu ikke slået op - eller opslaget svigtede og
   * prøves igen. Punktet kommer af sig selv.
   */
  awaitingLookup: number
}

export interface NearbyPoint {
  point: MapPoint
  /** Luftlinje - ikke køretid. */
  distanceKm: number
}

export interface NearbySuggestion {
  /** Hvad medarbejderen allerede skal derude. Tom = intet planlagt den dag. */
  originLabel: string
  latitude: number
  longitude: number
  radiusKm: number
  nearby: NearbyPoint[]
}

export interface GeocodeRun {
  found: number
  remaining: number
}

// --- Drift ---

export interface BackupStatus {
  ok: boolean
  ranAt?: string | null
  file?: string | null
  bytes: number
  message?: string | null
  ageHours?: number | null
  /** Der ligger ikke en frisk kopi - enten for gammel eller fejlet. */
  isStale: boolean
}

export interface DatabaseStatus {
  ok: boolean
  responseMs: number
  users: number
  error?: string | null
}

// --- Moduler (beslutning 048) ---

/** Et modul der kan slås til og fra i adminpanelet. */
export interface ModuleState {
  key: string
  name: string
  description: string
  /** Det, der gælder - også fra, hvis modulet, det er en del af, er slået fra. */
  enabled: boolean
  /** Modulet, det er en del af (intranettets funktioner, beslutning 119). */
  parent?: string | null
  /** Modulets egen knap. */
  enabledSelf?: boolean
  /** Kun Superadmin må tænde og slukke. */
  superadminOnly?: boolean
}

// --- Måleinstrumenter (beslutning 047) ---

export interface Instrument {
  id: number
  code: string
  name: string
  instrumentType?: string | null
  manufacturer?: string | null
  model?: string | null
  serialNumber?: string | null
  assignedUserId?: number | null
  assignedUserName?: string | null
  location?: string | null
  calibrationIntervalMonths?: number | null
  lastCalibrationDate?: string | null
  nextCalibrationDate?: string | null
  isActive: boolean
  /** Kalibreringen er løbet ud - målinger med det holder ikke. */
  isExpired: boolean
  isCalibrated: boolean
}

export interface InstrumentCalibration {
  id: number
  calibratedOn: string
  validUntil?: string | null
  performedBy?: string | null
  certificateNumber?: string | null
  notes?: string | null
  registeredByName?: string | null
  createdAt: string
}

export interface InstrumentDetail {
  instrument: Instrument
  notes?: string | null
  calibrations: InstrumentCalibration[]
}

// --- Systemindstillinger: Microsoft 365 ---

export interface DirectorySyncResult {
  success: boolean
  error?: string | null
  ranAt: string
  triggeredBy: string
  totalInDirectory: number
  created: number
  updated: number
  deactivated: number
  reactivated: number
  warnings: string[]
}

export interface Microsoft365Settings {
  tenantId?: string | null
  clientId?: string | null
  hasClientSecret: boolean
  allowedTenantIds?: string | null
  syncEnabled: boolean
  syncGroupId?: string | null
  syncIntervalMinutes: number
  syncDeactivateMissing: boolean
  syncDefaultRole: string
  /** Object id på teamet hvor sagernes kanaler oprettes. */
  teamsTeamId?: string | null
  /** Navnemønster med pladsholderne {id}, {navn} og {kunde}. */
  filesFolderNamePattern?: string | null
  /** Opret kanalen automatisk, når en sag oprettes. */
  teamsAutoCreateChannel: boolean
  isLoginConfigured: boolean
  isSyncConfigured: boolean
  /** Gemt opsætning afviger fra den kørende - API-et skal genstartes. */
  restartRequired: boolean
  /** Nøgler låst af environment variables. */
  lockedByEnvironment: string[]
  lastSync?: DirectorySyncResult | null
}

/**
 * Kontaktoplysninger til det sted, man skal ud til. Følger med alt det, en montør
 * åbner i marken, så nummeret kan ringes op og adressen åbnes i et kort
 * (docs/DECISIONS.md beslutning 025).
 */
export interface SiteContact {
  customerId?: number | null
  customerName?: string | null
  phone?: string | null
  contactPerson?: string | null
  address?: string | null
  postalCode?: string | null
  city?: string | null
}

/**
 * En enhed hos en kunde - et batteri, en tavle, et anlæg - der skal serviceres.
 * `code` er det der står i QR-koden på enhedens mærkat.
 */
export interface Device {
  id: number
  code: string
  name: string
  deviceType?: string | null
  serialNumber?: string | null
  customerId: number
  customerName?: string | null
  /** Sagen enheden blev installeret under, hvis nogen. */
  installedProjectId?: number | null
  location?: string | null
  /** Null = enheden er ikke på serviceaftale. */
  serviceIntervalMonths?: number | null
  nextServiceDate?: string | null
  lastServiceDate?: string | null
  isActive: boolean
  /** Hvor enheden står: dens egen adresse, ellers kundens (beslutning 109). */
  address?: string | null
  postalCode?: string | null
  city?: string | null
}

export interface DeviceDetail {
  device: Device
  manufacturer?: string | null
  model?: string | null
  notes?: string | null
  installedOn?: string | null
  installedProjectId?: number | null
  installedProjectName?: string | null
  createdAt: string
  updatedAt: string
  site?: SiteContact | null
  /** Enhedens eget sted. Null = enheden står på kundens adresse. */
  workSite?: ProjectSite | null
}

/**
 * Enhedens historik samlet ét sted - det montoeren faar at se efter et QR-scan
 * (beslutning 062). Alt findes i forvejen; her staar det bare samlet.
 */
export interface DeviceHistory {
  deviceId: number
  /** Sidste AFSLUTTEDE besoeg. Null = enheden er aldrig serviceret faerdig. */
  lastVisit?: DeviceHistoryVisit | null
  /** Antal afsluttede besoeg i alt. */
  visitCount: number
  /** Afvigelserne ved SIDSTE besoeg - ikke en samlet liste, se beslutning 062. */
  deviations: DeviceHistoryDeviation[]
  measurements: DeviceMeasurementSeries[]
  photos: DeviceHistoryPhoto[]
  warranties: DeviceHistoryWarranties
  defects: DeviceHistoryDefects
}

export interface DeviceHistoryVisit {
  id: number
  status: ServiceVisitStatus
  checklistName?: string | null
  performedBy?: UserRef | null
  startedAt: string
  completedAt?: string | null
  nextServiceDate?: string | null
  deviationCount: number
  notes?: string | null
  instrumentName?: string | null
  /** Var kalibreringen gyldig den dag? Null = intet instrument valgt. */
  instrumentWasValid?: boolean | null
}

export interface DeviceHistoryDeviation {
  visitId: number
  occurredAt: string
  text: string
  note?: string | null
  value?: number | null
  unit?: string | null
  minValue?: number | null
  maxValue?: number | null
  outOfRange: boolean
}

/** Ét maalepunkt fulgt gennem besoegene. Punkterne kobles paa tekst og enhed. */
export interface DeviceMeasurementSeries {
  text: string
  unit?: string | null
  minValue?: number | null
  maxValue?: number | null
  /** AEldste foerst. */
  points: DeviceMeasurementPoint[]
}

export interface DeviceMeasurementPoint {
  visitId: number
  measuredAt: string
  value: number
  outOfRange: boolean
}

export interface DeviceHistoryPhoto {
  visitId: number
  photoId: number
  kind: ServicePhotoKind
  name: string
  uploadedAt: string
}

/**
 * Et afsnit der kan mangle af andre grunde end "der er ingen data": modulet er
 * slukket, eller brugeren maa ikke se sagen. Et tomt afsnit uden forklaring
 * ville laeses som "der er ingenting".
 */
export interface DeviceHistorySection {
  available: boolean
  unavailableReason?: string | null
}

export interface DeviceHistoryWarranties extends DeviceHistorySection {
  items: Warranty[]
}

export interface DeviceHistoryDefects extends DeviceHistorySection {
  /** Sagen manglerne staar paa. Manglen hoerer til sagen, ikke til enheden. */
  projectId?: number | null
  projectName?: string | null
  items: Defect[]
}

/**
 * Serviceintervaller der følger af regler og af husets praksis.
 *
 * Nødbelysning: DS/EN 50172 kræver en prøve i hele den nominelle driftstid hvert
 * år, mens BR18 kun kræver installatørens service- og vedligeholdelseseftersyn
 * hvert andet. TC kører årligt, hvilket dækker begge dele. Bygningsejerens
 * månedlige og halvårlige prøver og det akkrediterede eftersyn hvert 4. år er
 * ikke vores opgave og hører derfor ikke til her.
 *
 * Nøglen sammenlignes uden hensyn til store/små bogstaver.
 */
export const serviceIntervalByDeviceType: { match: string; months: number; why: string }[] = [
  {
    match: 'nødbelysning',
    months: 12,
    why: 'Fuld driftstidsprøve hvert år (DS/EN 50172). Dækker samtidig BR18-kravet om installatøreftersyn mindst hvert 2. år.'
  },
  {
    match: 'brandvarsling',
    months: 24,
    why: 'Serviceeftersyn hvert 2. år. Bemærk: er anlægget et automatisk brandalarmanlæg (ABA), kræver DBI 005 årlig inspektion ved et akkrediteret firma.'
  },
  {
    match: 'indbrudsalarm',
    months: 12,
    why: 'Anlægsejeren skal have aftale om årligt serviceeftersyn ved en AIA-installatør registreret hos Forsikring & Pension.'
  },
  {
    match: 'varmepumpe',
    months: 12,
    why: 'Årligt eftersyn. Ikke et lovkrav i sig selv, men leverandørernes garanti falder typisk bort uden dokumenteret service. Er kølemiddelfyldningen på 5 tons CO2-ækvivalent eller derover, skal anlægget desuden lækagekontrolleres af en certificeret kølemontør.'
  }
]

/** Foreslået interval og begrundelse for en enhedstype, hvis der er en regel. */
export function suggestedServiceInterval(deviceType?: string | null) {
  if (!deviceType) return null

  const needle = deviceType.toLowerCase()
  return serviceIntervalByDeviceType.find((rule) => needle.includes(rule.match)) ?? null
}

/** De intervaller der tilbydes i brugerfladen. Feltet tager 1-120 måneder. */
export const serviceIntervals = [
  { months: 3, label: 'Hver 3. måned' },
  { months: 6, label: 'Hvert halve år' },
  { months: 12, label: 'Én gang om året' },
  { months: 24, label: 'Hvert andet år' },
  { months: 36, label: 'Hvert tredje år' }
]

// ---------------------------------------------------------------------------
// Service: checklister og besøg
// ---------------------------------------------------------------------------

/** "check" = i orden/afvigelse, "measurement" = et tal med en enhed. */
export type ServiceItemType = 'check' | 'measurement'

/** Svaret på et punkt. `na` = gælder ikke for denne enhed. */
export type ServiceItemResult = 'ok' | 'deviation' | 'na'

export type ServiceVisitStatus = 'in_progress' | 'completed' | 'cancelled'

/** De to fotos et besøg altid skal have. */
export type ServicePhotoKind = 'device' | 'label'

export interface ServiceChecklistItem {
  id: number
  sortOrder: number
  text: string
  itemType: ServiceItemType
  /** Enheden en måling angives i - V, A, °C. Kun for målepunkter. */
  unit?: string | null
  minValue?: number | null
  maxValue?: number | null
  isRequired: boolean
  helpText?: string | null
}

export interface ServiceChecklist {
  id: number
  name: string
  description?: string | null
  /** Enhedstypen listen foreslås til. Null = passer til alt. */
  deviceType?: string | null
  isActive: boolean
  sortOrder: number
  items: ServiceChecklistItem[]
}

export interface ServiceVisit {
  id: number
  deviceId: number
  deviceName?: string | null
  deviceCode?: string | null
  status: ServiceVisitStatus
  checklistName?: string | null
  performedBy?: UserRef | null
  startedAt: string
  completedAt?: string | null
  nextServiceDate?: string | null
  deviationCount: number
}

export interface ServiceVisitItem extends ServiceChecklistItem {
  /** Null = ikke besvaret endnu. */
  result?: ServiceItemResult | null
  value?: number | null
  note?: string | null
}

export interface ServiceVisitPhoto {
  id: number
  kind: ServicePhotoKind
  name: string
  webUrl?: string | null
  uploadedAt: string
}

export interface ServiceVisitDetail {
  visit: ServiceVisit
  notes?: string | null
  signedByName?: string | null
  signedAt?: string | null
  items: ServiceVisitItem[]
  photos: ServiceVisitPhoto[]
  site?: SiteContact | null
  // Måleinstrumentet der blev brugt (beslutning 047). Navn, serienummer og
  // datoer er KOPIER fra den dag - de slås ikke op igen.
  instrumentId?: number | null
  instrumentName?: string | null
  instrumentSerialNumber?: string | null
  instrumentCalibratedOn?: string | null
  instrumentCalibrationValidUntil?: string | null
  /** Var kalibreringen gyldig på besøgets dato? Null = intet instrument valgt. */
  instrumentWasValid?: boolean | null
}

/** Hvad de to billeder skal forestille - teksten står på knapperne. */
export const servicePhotoLabels: { kind: ServicePhotoKind; label: string; hint: string }[] = [
  { kind: 'device', label: 'Foto af enheden', hint: 'Hvordan ser den ud nu?' },
  { kind: 'label', label: 'Foto af servicemærkatet', hint: 'Med den nye dato klippet i' }
]

/** Kategorierne for dokumentation på en sag. Skal matche ProjectFileCategories i backenden. */
export const FileCategories = {
  before: 'before',
  after: 'after',
  panel: 'panel',
  receipt: 'receipt',
  other: 'other'
} as const

export type FileCategory = (typeof FileCategories)[keyof typeof FileCategories]

/** Dansk navn til hver kategori, i den rækkefølge de vises. */
export const fileCategoryLabels: { value: FileCategory; label: string }[] = [
  { value: 'before', label: 'Før' },
  { value: 'after', label: 'Efter' },
  { value: 'panel', label: 'Tavle' },
  { value: 'receipt', label: 'Kvittering' },
  { value: 'other', label: 'Andet' }
]

/** Kategorier, kun systemet lægger på (egenkontrol 131, diagram 150) - vises, men kan ikke vælges. */
export const systemFileCategoryLabels: Record<string, string> = {
  selfcheck: 'Egenkontrol',
  diagram: 'Diagram'
}

/** Kategoriens danske navn - også for dem, kun systemet lægger på. */
export const fileCategoryLabel = (category: string) =>
  fileCategoryLabels.find((c) => c.value === category)?.label ?? systemFileCategoryLabels[category] ?? category

// ---------- Diagrammer på sagen (beslutning 150) ----------

export type DiagramElementKind = 'line' | 'meter' | 'breaker' | 'inverter' | 'box' | 'text'

/** Ét element på tegningen. Linjer bruger x/y til x2/y2; alt andet en kasse fra x/y med w/h. */
export interface DiagramElement {
  kind: DiagramElementKind
  x: number
  y: number
  w?: number
  h?: number
  x2?: number
  y2?: number
  label?: string | null
  dashed?: boolean
  arrow?: boolean
  thick?: boolean
  size?: number
  bold?: boolean
  highlight?: boolean
  /** Teksten flyttet væk fra sin normale plads (beslutning 150). */
  lx?: number
  ly?: number
}

export interface DiagramHeader {
  title?: string | null
  address?: string | null
  installNumber?: string | null
  installer?: string | null
  extra?: string | null
}

export interface DiagramContent {
  header?: DiagramHeader | null
  elements?: DiagramElement[] | null
}

export interface DiagramSummary {
  id: number
  projectId: number
  projectName: string
  projectOrderNumber?: string | null
  title: string
  createdAt: string
  updatedAt: string
  createdBy?: string | null
  updatedBy?: string | null
  pdfSavedAt?: string | null
  pdfFileId?: number | null
  uploadError?: string | null
}

export interface Diagram {
  summary: DiagramSummary
  content: DiagramContent
  projectAddress?: string | null
}

/**
 * En fil eller et foto på en sag. Selve filen ligger i sagens mappe i SharePoint;
 * indholdet hentes gennem API-et på /api/projects/{id}/files/{fileId}/content.
 */
export interface ProjectFile {
  id: number
  name: string
  contentType: string
  sizeBytes: number
  category: FileCategory
  caption?: string | null
  /** Link til filen i SharePoint/Teams. */
  webUrl?: string | null
  isImage: boolean
  uploadedBy?: UserRef | null
  uploadedAt: string
}

/** Hvad alle brugere må se af filopsætningen: om der overhovedet kan gemmes filer. */
export interface FilesSettings {
  isConfigured: boolean
}

export interface ConnectionTest {
  success: boolean
  message: string
  userCount: number
}

/**
 * Et abonnement - en løbende aftale kunden betaler fast for (beslutning 031).
 * Datoerne er rene datoer ("2026-01-01"), ikke tidspunkter.
 */
export interface Subscription {
  id: number
  customerId: number
  customerName: string
  /** Sat = serviceaftale på netop det anlæg. Null = fast beløb for kunden. */
  deviceId?: number | null
  deviceName?: string | null
  deviceCode?: string | null
  description: string
  /** Beløb pr. periode UDEN moms - momsen lægges på i økonomisystemet. */
  amount: number
  intervalMonths: number
  startDate: string
  nextInvoiceDate: string
  endDate?: string | null
  isPaused: boolean
  notes?: string | null
}

/** Ét faktureringsgrundlag: "der skal sendes en regning for denne periode". */
export interface SubscriptionInvoice {
  id: number
  subscriptionId: number
  periodStart: string
  periodEnd: string
  amount: number
  description: string
  status: 'pending' | 'exported' | 'failed' | 'cancelled'
  externalId?: string | null
  exportedAt?: string | null
  exportError?: string | null
  createdAt: string
  customerName: string
}

export interface SubscriptionDetail {
  subscription: Subscription
  invoices: SubscriptionInvoice[]
}

/** Resultatet af en faktureringskørsel. */
export interface BillingRunResult {
  created: number
  /** Samlet beløb uden moms. */
  total: number
  /** Aftaler der forfaldt, men blev sprunget over (på pause eller udløbet). */
  skipped: number
}

/** Til/fra og klokkeslæt for den automatiske fakturering. Står som standard til fra. */
export interface BillingSettings {
  enabled: boolean
  /** Klokkeslæt i dansk tid, 0-23. */
  runHour: number
}

/** Om afleveringen til økonomisystemet er klar til brug. */
export interface ExportStatus {
  systemName: string
  isConfigured: boolean
  pendingCount: number
}

/** De faktureringsintervaller brugerfladen tilbyder. Skal matche SubscriptionIntervals i backenden. */
export const subscriptionIntervals = [
  { months: 1, label: 'Hver måned' },
  { months: 3, label: 'Hvert kvartal' },
  { months: 6, label: 'Hvert halve år' },
  { months: 12, label: 'Én gang om året' }
]

/** Opsætningen af de automatiske deadline-varsler (kravspec afsnit 29). */
export interface DeadlineAlertSettings {
  enabled: boolean
  /** Antal dage før deadline der varsles. 1-30. */
  warningDays: number
  /** Dage før en kalibrering udløber, der varsles (beslutning 049). */
  calibrationWarningDays: number
  /** Klokkeslæt i dansk tid hvor dagens varsler sendes. 0-23. */
  runHour: number
}

/** Opsætningen af morgenbeskeden med dagens plan (beslutning 081). */
export interface MorningBriefSettings {
  /** Som standard fra. */
  enabled: boolean
  /** Klokkeslæt i dansk tid. 0-23. */
  runHour: number
}

/** Resultatet af en kørsel af morgenbeskeden. */
export interface MorningBriefResult {
  /** Falsk når morgenbeskeden er slået fra. */
  ran: boolean
  recipients: number
  /** Antal der intet fik, fordi de ikke havde noget i dag. */
  skipped: number
}

/** Resultatet af en kørsel af deadline-varslerne. */
export interface DeadlineAlertResult {
  /** Falsk når varslerne er slået fra. */
  ran: boolean
  soon: number
  overdue: number
  recipients: number
  total: number
}

export interface Role {
  id: number
  name: string
  description?: string | null
  entraGroupId?: string | null
  isSystem: boolean
  userCount: number
}

// --- Kunder ---

export interface Customer {
  id: number
  name: string
  contactPerson?: string | null
  email?: string | null
  phone?: string | null
  address?: string | null
  postalCode?: string | null
  city?: string | null
  fullAddress?: string | null
  reference?: string | null
  notes?: string | null
  isActive: boolean
  projectCount: number
  activeProjectCount: number
  /** Rettigheder i ampere på kundens adresse, fx 63 (beslutning 109). */
  connectionAmps?: number | null
}

export interface CustomerDetail {
  customer: Customer
  projects: Project[]
}

/**
 * Hvad der står i vejen for at slette kunden, og hvad der følger med (beslutning 079).
 * De fem første står i vejen; garantier og nøgler slettes med; henvendelser og beskeder
 * til marken mister koblingen.
 */
export interface CustomerDeleteSummary {
  projects: number
  devices: number
  subscriptions: number
  warrantiesWithClaims: number
  keysOnLoan: number
  warranties: number
  accessKeys: number
  supportTickets: number
  fieldMessages: number
  /** VPN-sites (beslutning 091) står i vejen. */
  vpnSites?: number
  /** Serverens forklaring. Null = kunden kan slettes. */
  blockedReason?: string | null
  canDelete: boolean
}

// --- Procedureskabeloner ---

/** En procedure = en type sag med en eller flere skabeloner (beslutning 141). */
export interface Procedure {
  id: number
  name: string
  description?: string | null
  isActive: boolean
  sortOrder: number
  templateCount: number
}

export interface ProjectTemplateTask {
  id: number
  /** Hovedemnet (beslutning 141). Null på skabeloner fra før hovedemnerne. */
  section?: string | null
  title: string
  description?: string | null
  sortOrder: number
  /** Underemnets andel i procent - alle underemner giver 100 tilsammen. */
  weight: number
  priority?: Lookup | null
  defaultAssignee?: UserRef | null
  /** Fast ansvarligt team i stedet for en person (beslutning 142). */
  defaultTeam?: { id: number; name: string } | null
  /** Deadline = projektets startdato + dette antal dage. */
  deadlineOffsetDays?: number | null
}

export interface ProjectTemplate {
  id: number
  name: string
  description?: string | null
  category?: string | null
  procedureId?: number | null
  procedureName?: string | null
  defaultPriority?: Lookup | null
  defaultDurationDays?: number | null
  isActive: boolean
  sortOrder: number
  taskCount: number
  tasks: ProjectTemplateTask[]
}

// ---------- Manualer (beslutning 063) ----------

/** Skrevet her i systemet, eller en uploadet faerdig fil. */
export type ManualKind = 'written' | 'file'

/**
 * En manual. Der staar ALTID hvem der har lavet den, og hvem der sidst rettede -
 * en manual er en forskrift, og man skal kunne finde den, der ved hvorfor.
 */
export interface Manual {
  id: number
  title: string
  summary?: string | null
  kind: ManualKind
  kindName: string
  categoryId?: number | null
  categoryName?: string | null
  fileName?: string | null
  contentType?: string | null
  sizeBytes?: number | null
  /** Link til filen i SharePoint. */
  webUrl?: string | null
  isActive: boolean
  createdBy?: UserRef | null
  createdAt: string
  updatedBy?: UserRef | null
  updatedAt: string
  /** Enhedstyperne manualen hoerer til - tom = ingen kobling. */
  deviceTypes: string[]
  /** Procedureskabelonerne (sagstyperne) manualen hoerer til. */
  templates: ManualTemplateRef[]
  imageCount: number
}

export interface ManualTemplateRef {
  id: number
  name: string
}

export interface ManualDetail {
  manual: Manual
  /** Teksten. Null for en uploadet fil - der ER filen indholdet. */
  content?: string | null
  images: ManualImage[]
}

/** Et billede i en skrevet manual. Refereres fra teksten som `billede:{id}`. */
export interface ManualImage {
  id: number
  name: string
  contentType: string
  sizeBytes: number
  uploadedBy?: UserRef | null
  uploadedAt: string
}

/** En hylde i biblioteket. */
export interface ManualCategory {
  id: number
  name: string
  sortOrder: number
  isActive: boolean
  manualCount: number
}

export interface ManualInput {
  title: string
  summary?: string | null
  kind: ManualKind
  categoryId?: number | null
  content?: string | null
  isActive: boolean
  deviceTypes: string[]
  templateIds: number[]
}

export interface ManualCategoryInput {
  name: string
  sortOrder: number
  isActive: boolean
}

// ---------- Lager (beslutning 069) ----------

/** Enheden en vare taelles i. */
export interface StockUnit {
  code: string
  name: string
}

/**
 * En vare i det faelles kartotek.
 *
 * To slags koder, og de goer ikke det samme: `barcode` er leverandoerens EAN fra
 * emballagen (kan scannes uden at noget maerkes op), `code` er husets egen, som
 * kan printes paa et maerkat til det, der ikke har en stregkode.
 */
export interface StockItem {
  id: number
  code: string
  barcode?: string | null
  supplierNumber?: string | null
  name: string
  description?: string | null
  unit: string
  unitName: string
  supplier?: string | null
  category?: string | null
  /** Kostpris pr. enhed, UDEN moms. Null = prisen er ikke kendt. */
  costPrice?: number | null
  priceUpdatedAt?: string | null
  isActive: boolean
  createdBy?: UserRef | null
  createdAt: string
  updatedAt: string
}

/** Hvad et scan viste sig at vaere. */
export type ScanKind = 'item' | 'barcode_unknown' | 'unknown'

export interface StockScanResult {
  kind: ScanKind
  item?: StockItem | null
  /** Det systemet faktisk laeste - saa brugeren kan se hvad der skete. */
  scannedCode: string
  message?: string | null
}

export interface StockSuggestions {
  suppliers: string[]
  categories: string[]
}

export interface StockItemInput {
  name: string
  description?: string | null
  barcode?: string | null
  supplierNumber?: string | null
  unit: string
  supplier?: string | null
  category?: string | null
  costPrice?: number | null
  isActive: boolean
}

/** Slags sted varer kan ligge. */
export type StockLocationKind = 'warehouse' | 'van' | 'box'

/**
 * Et sted varer kan ligge - lager, bil eller kasse (beslutning 070).
 *
 * De tre er den samme slags ting: navn, QR-maerkat og et indhold. Forskellen er
 * hvad de hedder, og om de kan ligge inde i noget andet.
 */
export interface StockLocation {
  id: number
  code: string
  name: string
  kind: StockLocationKind
  kindName: string
  /** Hvad kassen ligger i. Null for lager og bil. */
  parentId?: number | null
  parentName?: string | null
  responsibleUser?: UserRef | null
  reference?: string | null
  notes?: string | null
  isActive: boolean
  boxCount: number
  /** Antal forskellige varer - INKLUSIVE kasserne, naar det er en bil eller et lager. */
  itemCount: number
}

/** Hvad der ligger ét sted. */
export interface StockLevel {
  itemId: number
  itemName: string
  itemCode: string
  barcode?: string | null
  supplierNumber?: string | null
  unit: string
  unitName: string
  /** Antallet. **Kan vaere negativt** - det er selve advarslen om, at noget ikke er scannet ind. */
  quantity: number
  minQuantity?: number | null
  isBelowMinimum: boolean
  locationId: number
  locationName: string
  locationKind: StockLocationKind
  updatedAt: string
}

export interface StockLocationDetail {
  location: StockLocation
  boxes: StockLocation[]
  levels: StockLevel[]
}

export interface StockItemStock {
  itemId: number
  total: number
  levels: StockLevel[]
}

export type StockMovementKind = 'receipt' | 'move' | 'consumption' | 'count' | 'removal'

export interface StockMovement {
  id: number
  itemId: number
  itemName: string
  unit: string
  fromLocationId?: number | null
  fromLocationName?: string | null
  toLocationId?: number | null
  toLocationName?: string | null
  quantity: number
  kind: StockMovementKind
  kindName: string
  projectId?: number | null
  projectName?: string | null
  /** Beholdningen efter bevaegelsen. */
  resultingQuantity?: number | null
  note?: string | null
  createdBy?: UserRef | null
  createdAt: string
}

export interface StockLocationInput {
  name: string
  kind: StockLocationKind
  parentId?: number | null
  responsibleUserId?: number | null
  reference?: string | null
  notes?: string | null
  isActive: boolean
}

export interface StockMoveInput {
  itemId: number
  fromLocationId?: number | null
  toLocationId?: number | null
  quantity: number
  kind: StockMovementKind
  projectId?: number | null
  note?: string | null
}

// ---------- Lager, etape 3: marken (beslutning 071) ----------

/** En sag som den vises i markens vaelger. */
export interface StockFieldProject {
  id: number
  name: string
  customerName?: string | null
}

/**
 * Alt markens side skal vide, foer der scannes.
 *
 * `myLocation` er bilen, brugeren staar som ansvarlig for. **Null er et gyldigt
 * svar** - ikke alle har en bil, og saa skal der vaelges frem for at gaettes.
 */
export interface StockFieldContext {
  myLocation?: StockLocation | null
  locations: StockLocation[]
  projects: StockFieldProject[]
}

/** Hvad der er gaaet til en sag, lagt sammen pr. vare. Kun antal - ingen vaerdi. */
export interface StockProjectMaterial {
  itemId: number
  itemName: string
  itemCode: string
  unit: string
  unitName: string
  /** Samlet forbrug. **Kan blive negativt**, hvis en fejlscanning er rettet. */
  quantity: number
  lastUsedAt: string
}

export interface StockProjectMaterials {
  materials: StockProjectMaterial[]
  /** Hvor mange enkeltbevaegelser der ligger bag. */
  movementCount: number
}

/** En scannet vare paa vej ned i kurven. */
export interface StockBatchLineInput {
  itemId: number
  quantity: number
}

/**
 * Flere varer registreret i én handling.
 *
 * Retningen ligger i fra/til, praecis som ved en enkelt bevaegelse - bundtet er
 * den samme handling gentaget, ikke en ny slags.
 */
export interface StockBatchInput {
  kind: StockMovementKind
  fromLocationId?: number | null
  toLocationId?: number | null
  projectId?: number | null
  note?: string | null
  lines: StockBatchLineInput[]
}

/** Kvitteringen for et bundt: hvad der faktisk blev skrevet. */
export interface StockBatchResult {
  /** Antal varer EFTER sammenlaegning - den samme vare scannet to gange taeller én. */
  itemCount: number
  totalQuantity: number
  movements: StockMovement[]
}

// ---------- Lager, etape 4: prisfiler (beslutning 072) ----------

/** Hvad en vare kendes igen paa i prisfilen. */
export type PriceMatchKey = 'supplier_number' | 'barcode'

export interface PriceMatchKeyOption {
  code: PriceMatchKey
  name: string
}

/**
 * Kolonneopsaetningen for én leverandoers prisfil.
 *
 * **Formatet staar i databasen, ikke i koden**: kolonnerne peges ud én gang og
 * gemmes, saa en ny grossist er en ny opsaetning - ikke en ny udgave af systemet.
 */
export interface PriceImportProfile {
  id: number
  name: string
  /** Tom = filen kan ramme alle varer. Ellers kun den leverandoers varer. */
  supplier?: string | null
  /** Tom = systemet finder selv skilletegnet. */
  delimiter?: string | null
  hasHeader: boolean
  matchKey: PriceMatchKey
  matchKeyName: string
  /** Kolonnenavn fra overskriftsraekken, eller et nummer fra 1. */
  keyColumn: string
  priceColumn: string
  nameColumn?: string | null
  /** Rabat der traekkes fra filens pris. **Findes fordi grossister sender bruttopriser.** */
  discountPercent?: number | null
  /** Er tallene skrevet med komma som decimaltegn? */
  decimalComma: boolean
  createdBy?: UserRef | null
  updatedAt: string
}

export interface PriceImportProfileInput {
  name: string
  supplier?: string | null
  delimiter?: string | null
  hasHeader: boolean
  matchKey: PriceMatchKey
  keyColumn: string
  priceColumn: string
  nameColumn?: string | null
  discountPercent?: number | null
  decimalComma: boolean
}

export type PriceImportState = 'preview' | 'applied' | 'undone'

/** Én koersel af en prisfil. */
export interface PriceImport {
  id: number
  fileName: string
  profileName?: string | null
  discountPercent?: number | null
  state: PriceImportState
  stateName: string
  rowCount: number
  matchedCount: number
  changedCount: number
  /** Linjer hvor prisen springer mere end 50 %. **Her viser bruttopris-fejlen sig.** */
  largeChangeCount: number
  createdBy?: UserRef | null
  createdAt: string
  appliedAt?: string | null
}

/** Én linje fra filen, som den blev laest og matchet. */
export interface PriceImportLine {
  id: number
  rowNumber: number
  key?: string | null
  fileName?: string | null
  itemId?: number | null
  itemName?: string | null
  filePrice?: number | null
  oldPrice?: number | null
  newPrice?: number | null
  applied: boolean
  isLargeChange: boolean
  /** Hvorfor linjen ikke giver en aendring. Null = klar. */
  problem?: string | null
}

export interface PriceImportDetail {
  import: PriceImport
  /** Linjer der ville aendre en pris - de store spring foerst. */
  changes: PriceImportLine[]
  problems: PriceImportLine[]
  /** Hvor mange problemer der er i alt - listen er afkortet. */
  problemCount: number
}

export interface PriceImportUndoResult {
  restored: number
  /** Varer der blev sprunget over, fordi prisen er rettet i haanden siden. */
  skipped: number
}

// ---------- Support: henvendelser (beslutning 073) ----------

export type SupportType = 'customer' | 'system'
export type SupportState = 'new' | 'open' | 'waiting' | 'resolved' | 'closed'
export type SupportSource = 'manual' | 'mail'

export interface SupportTypeOption {
  code: SupportType
  name: string
  icon: string
}

export interface SupportStateOption {
  code: SupportState
  name: string
  color: string
  isOpen: boolean
}

/**
 * En henvendelse.
 *
 * **Hullet den lukker**: der fandtes fem steder at skrive et problem ned, men
 * intet sted til noget, der er meldt ind og **endnu ikke besluttet**.
 */
export interface SupportTicket {
  id: number
  /** Nummeret man siger i telefonen: "det er sag 42". */
  number: number
  type: SupportType
  typeName: string
  subject: string
  state: SupportState
  stateName: string
  stateColor: string
  isOpen: boolean
  priorityId?: number | null
  priorityName?: string | null
  priorityColor?: string | null
  customerId?: number | null
  customerName?: string | null
  projectId?: number | null
  projectName?: string | null
  deviceId?: number | null
  deviceName?: string | null
  reportedBy?: UserRef | null
  reporterName?: string | null
  reporterEmail?: string | null
  reporterPhone?: string | null
  assignee?: UserRef | null
  source: SupportSource
  sourceName: string
  /** Hvad den blev til, i klar tekst: "Sag: Tavle, Vestergade". */
  convertedTo?: string | null
  messageCount: number
  /** Timer siden sidste bevaegelse. **Det tal siger, om indbakken passes.** */
  waitingSinceHours: number
  createdAt: string
  updatedAt: string
  firstResponseAt?: string | null
  resolvedAt?: string | null
  closedAt?: string | null
}

/** Én besked i traaden. Teksten er ALTID ren tekst - aldrig HTML. */
export interface SupportMessage {
  id: number
  body: string
  /** En note kun huset ser. Er den falsk, er beskeden sendt ud. */
  isInternal: boolean
  authorUser?: UserRef | null
  authorName?: string | null
  authorEmail?: string | null
  source: SupportSource
  /** Vedhaeftede filer mailen havde. **De hentes ikke** - de ligger i postkassen. */
  attachmentCount: number
  createdAt: string
}

export interface SupportTicketDetail {
  ticket: SupportTicket
  messages: SupportMessage[]
  /** Maa brugeren tildele, lukke og svare udadtil? */
  canHandle: boolean
}

export interface SupportSummary {
  /** Henvendelser ingen har set paa. **Det er de farlige.** */
  new: number
  open: number
  waiting: number
  mineOpen: number
}

export interface SupportTicketInput {
  type: SupportType
  subject: string
  body: string
  priorityId?: number | null
  customerId?: number | null
  projectId?: number | null
  deviceId?: number | null
  assigneeId?: number | null
  reporterName?: string | null
  reporterEmail?: string | null
  reporterPhone?: string | null
}

/** Det der ikke sendes, staar uaendret. 0 fjerner en kobling. */
export interface SupportTicketUpdate {
  subject?: string | null
  state?: SupportState | null
  priorityId?: number | null
  customerId?: number | null
  projectId?: number | null
  deviceId?: number | null
  assigneeId?: number | null
  reporterName?: string | null
  reporterEmail?: string | null
  reporterPhone?: string | null
}

export interface SupportMessageResult {
  message: SupportMessage
  /** Blev svaret ogsaa sendt som mail? */
  mailSent: boolean
  /** Hvorfor mailen ikke gik. Beskeden er gemt alligevel. */
  mailError?: string | null
}

export interface SupportMailSettings {
  enabled: boolean
  mailbox?: string | null
  folder?: string | null
  intervalMinutes: number
  defaultType: SupportType
  isConfigured: boolean
  /** Er Microsoft 365 sat op? Uden det kan postkassen ikke laeses. */
  microsoftReady: boolean
}

export interface SupportMailSettingsInput {
  enabled: boolean
  mailbox?: string | null
  folder?: string | null
  intervalMinutes: number
  defaultType: SupportType
}

export interface SupportMailRun {
  created: number
  appended: number
  skipped: number
  error?: string | null
}

// ---------- TV-klient: opdatering fra serveren (beslutning 074) ----------

/** Den TV-klient, serveren har klar. Pakken laves af udrulningen. */
export interface TvClientPackage {
  available: boolean
  version?: string | null
  sha256?: string | null
  size?: number | null
  /** Hvornaar pakken blev lagt op - altsaa hvornaar der sidst blev udrullet. */
  builtAt?: string | null
  /** Aktive skaerme, der koerer en aeldre version. */
  screensBehind: number
}

export interface ScreenUpdateAllResult {
  sent: number
  version: string
}

// ---------- TV-skaerme: tilknytning med kode (beslutning 075) ----------

/** Koden fra TV'et og hvilken skaerm den skal tilknyttes. Tom screenId = opret en ny. */
export interface ScreenPairingClaimInput {
  code: string
  screenId?: number | null
  name?: string | null
  location?: string | null
  dashboardId?: number | null
}

// ---------- Raspberry Pi-image (beslutning 075) ----------

/** Imaget til nye skaerme og status fra byggeren paa serveren. */
export interface PiImage {
  available: boolean
  fileName?: string | null
  clientVersion?: string | null
  baseImage?: string | null
  sha256?: string | null
  size?: number | null
  builtAt?: string | null
  /** building, ok eller failed - null hvis byggeren aldrig har koert. */
  buildState?: 'building' | 'ok' | 'failed' | null
  buildStartedAt?: string | null
  buildFinishedAt?: string | null
  buildMessage?: string | null
}

/** Kortlivet link til at hente imaget. Adressen er relativ til API-et. */
export interface PiImageLink {
  url: string
  expiresAt: string
}

// ---------- Sletning af sager (beslutning 076) ----------

/** Hvad der forsvinder - og hvad der bliver - hvis sagen slettes. */
export interface ProjectDeleteSummary {
  /** Slettes med sagen. */
  tasks: number
  comments: number
  files: number
  calendarEntries: number
  defects: number
  /** Bliver, men mister koblingen til sagen. */
  devices: number
  stockMovements: number
  supportTickets: number
  warranties: number
}

// ---------- Hurtigknappen i marken (beslutning 082) ----------

/** En sag hurtignoten kan laegges paa. */
export interface QuickNoteTarget {
  projectId: number
  name: string
  customerName?: string | null
  orderNumber?: string | null
  address?: string | null
  /** Luftlinje fra telefonen i km - null naar sagen ingen position har. */
  distanceKm?: number | null
  /** Sagen staar i min egen kalender i dag. */
  plannedToday: boolean
  statusName: string
  statusColor: string
}

/** Sagerne knappen foreslaar, og den ene der er valgt paa forhaand. */
export interface QuickNoteTargets {
  targets: QuickNoteTarget[]
  /** null naar systemet ikke er sikkert nok - saa skal montoeren selv vaelge. */
  selectedProjectId?: number | null
  hasPosition: boolean
  withoutPosition: number
}

/** Hvad der blev skrevet, og hvem der fik besked. */
export interface QuickNoteResult {
  projectId: number
  projectName: string
  commentId?: number | null
  fileId?: number | null
  notifiedUserNames: string[]
}

// ---------- Kundens eget sagsnummer (beslutning 083) ----------

/** De andre sager hos samme kunde, der allerede koerer paa sagsnummeret. */
export interface CaseNumberInUse {
  projects: string[]
}

// ---------- Porte og nøglebokse på sagen (beslutning 088) ----------

export type AccessCodeKind = 'gate' | 'keybox'

/** En port eller nøgleboks - UDEN koden. Den hentes kun med "Vis kode". */
export interface ProjectAccessCode {
  id: number
  kind: AccessCodeKind
  kindLabel: string
  name: string
  note?: string | null
  updatedByName?: string | null
  updatedAt: string
}

/** Opret eller ret. Ved en rettelse betyder et felt, der ikke er med, "lad stå" - også koden. */
export interface ProjectAccessCodeInput {
  kind?: AccessCodeKind
  name?: string
  note?: string | null
  code?: string
}

// ---------- VPN til kundeanlæg (beslutning 091) ----------

export interface VpnEndpoint {
  host: string
  port: number
  serverPublicKey: string
  /** Det net, en klient skal sende gennem tunnelen, f.eks. "10.50.0.0/16". */
  allowedIps: string
}

export interface VpnInterfaceHealth {
  name: string
  peers: number
  online: number
}

export interface VpnGatewayHealth {
  status: string
  version: string
  schemaVersion?: string | null
  wgSites: VpnInterfaceHealth
  wgTechs: VpnInterfaceHealth
  activeSessions: number
  lastBackupAt?: string | null
  serverTime: string
}

export interface VpnStatus {
  baseUrl: string
  hasClientCertificate: boolean
  hasClientKey: boolean
  hasCaCertificate: boolean
  /** Null, når gatewayen ikke kunne nås - se gatewayError. */
  health?: VpnGatewayHealth | null
  gatewayError?: string | null
  /** Kræver VPN-handlinger sikkerhedsnøgle (beslutning 092)? */
  stepUpEnabled: boolean
  /** Authentication context i Entra, f.eks. "c1". */
  stepUpAuthContextId?: string | null
}

/** online, offline, pending_key, disabled - eller missing/unknown, når gatewayen ikke kender sitet eller ikke svarer. */
export type VpnSiteStatus = 'online' | 'offline' | 'pending_key' | 'disabled' | 'missing' | 'unknown'

export interface VpnSite {
  id: number
  gatewaySiteId: string
  name: string
  customerId: number
  customerName: string
  deviceId?: number | null
  deviceName?: string | null
  vpnAddress?: string | null
  status: VpnSiteStatus
  lastHandshakeAt?: string | null
  routerPublicKey?: string | null
  createdAt: string
  /** Det routeren forbinder til. Null, når gatewayen ikke svarer. */
  endpoint?: VpnEndpoint | null
  /** Sitets LAN-alias på gatewayen, fx "10.52.0.0/24" - det montøren skriver (beslutning 094). */
  lanPrefix?: string | null
  /** Kundens LAN bag routeren (/24). Null = LAN-adgang er ikke sat op. */
  customerLan?: string | null
  /** Routerens egen adresse på kundens LAN (standard .240). Montøren optræder som den. */
  routerLanAddress?: string | null
  /** Routerens interface mod kundens LAN, fx "eth1". */
  routerLanInterface?: string | null
  /** Montøren, der har sitet lige nu - kun én ad gangen. */
  inUseBy?: string | null
  inUseUntil?: string | null
  /** Sidste gang gatewayen pingede sitets alias for at se, om routeren er sat op (beslutning 095). */
  lanCheckedAt?: string | null
  /** Svarede routeren ved sidste tjek? Null = aldrig tjekket. */
  lanCheckOk?: boolean | null
  /** Routeren selv som enhed hos kunden (beslutning 105). Null på sites fra før. */
  routerDeviceId?: number | null
  routerDeviceName?: string | null
  /** Adressen, hvor sitet er sat op - fra felterne eller læst ud af navnet. */
  address?: string | null
  postalCode?: string | null
  city?: string | null
}

/** Svar fra "Tjek opsætningen": gatewayen har pinget sitets alias (beslutning 095). */
export interface VpnLanCheck {
  reachable: boolean
  address: string
  rttMs?: number | null
  message: string
  checkedAt: string
}

export interface VpnSiteList {
  sites: VpnSite[]
  gatewayError?: string | null
  /** Sites på gatewayen, som Overblik ikke kender. Bør være 0. */
  unknownOnGateway: number
}

export interface VpnTechnician {
  userId: number
  displayName: string
  email?: string | null
  vpnAddress: string
  revoked: boolean
  lastHandshakeAt?: string | null
  updatedAt: string
  userIsActive: boolean
}

/** pending = godkendt, venter på at montøren åbner den (beslutning 092). */
export type VpnSessionStatus = 'pending' | 'not_activated' | 'cancelled' | 'active' | 'expired' | 'revoked' | 'unknown'

export interface VpnSession {
  id: number
  vpnSiteId?: number | null
  siteName: string
  customerName?: string | null
  userId?: number | null
  technicianName: string
  grantedByName?: string | null
  projectId?: number | null
  projectName?: string | null
  reason: string
  durationMinutes: number
  /** Null, indtil montøren har åbnet adgangen. */
  startsAt?: string | null
  expiresAt?: string | null
  status: VpnSessionStatus
  endReason?: string | null
  siteAddress?: string | null
  technicianAddress?: string | null
  grantedAt: string
  activateBefore: string
  activatedAt?: string | null
}

export interface VpnSessionList {
  sessions: VpnSession[]
  gatewayError?: string | null
}

export interface VpnMySession {
  id: number
  siteName: string
  customerName?: string | null
  expiresAt: string
  /** Routerens VPN-adresse - dens egen web/SSH. */
  routerAddress?: string | null
  /** Aliasset for kundens LAN, fx "10.52.0.0/24". Null, hvis LAN-adgang ikke er sat op. */
  lanPrefix?: string | null
  customerLan?: string | null
  routerLanAddress?: string | null
}

/** Godkendt af en Superadmin - montøren åbner den selv (beslutning 092). */
export interface VpnPendingGrant {
  id: number
  siteName: string
  customerName?: string | null
  durationMinutes: number
  reason: string
  grantedByName?: string | null
  activateBefore: string
  /** Routerens VPN-adresse - vises før montøren forbinder. */
  routerAddress?: string | null
  /** Aliasset for kundens LAN på dette site. Null, hvis LAN-adgang ikke er sat op. */
  lanPrefix?: string | null
  customerLan?: string | null
  routerLanAddress?: string | null
}

export interface VpnMe {
  hasProfile: boolean
  revoked: boolean
  vpnAddress?: string | null
  publicKey?: string | null
  lastHandshakeAt?: string | null
  endpoint?: VpnEndpoint | null
  activeSessions: VpnMySession[]
  gatewayError?: string | null
  pendingGrants?: VpnPendingGrant[] | null
  /** Kræver det sikkerhedsnøgle at åbne adgang og lave nøgle? */
  stepUpRequired: boolean
  /** Konfigurationen er lavet før LAN-adgang og mangler LAN-aliasserne - lav en ny nøgle. */
  configMissingLan?: boolean
}

// ---------------------------------------------------------------------------
// Udskiftede dele paa servicesagen (beslutning 098)
// ---------------------------------------------------------------------------

/** Hvad et billede af en udskiftet del viser (beslutning 112). */
export type ReplacedPartPhotoKind = 'approval' | 'after' | 'photo'

export interface ReplacedPartPhoto {
  id: number
  /** Ligger bag login - hentes med useAuthedBlob. */
  url: string
  /** Godkendelse, efter udskiftning, eller et billede fra før beslutning 112. */
  kind: ReplacedPartPhotoKind
  /** Filen i SharePoint. Null for ældre billeder, der ligger i databasen. */
  webUrl?: string | null
}

export interface ReplacedPart {
  id: number
  projectId: number
  projectName: string
  deviceId?: number | null
  deviceName?: string | null
  description: string
  quantity: number
  unit: string
  unitName: string
  stockItemId?: number | null
  itemName?: string | null
  barcode?: string | null
  /** Bilen eller lageret, delen blev trukket fra. Null = ikke fra lageret. */
  fromLocationName?: string | null
  createdBy?: UserRef | null
  createdAt: string
  canDelete: boolean
  photos: ReplacedPartPhoto[]
  /** Serienummeret på den udtagne del (beslutning 112). */
  oldSerialNumber?: string | null
  /** Serienummeret på den nye del (beslutning 112). */
  newSerialNumber?: string | null
}

export interface ReplacedPartInput {
  deviceId: number | null
  description: string
  quantity: number
  stockItemId: number | null
  itemName: string | null
  barcode: string | null
  unit: string | null
  fromLocationId: number | null
  oldSerialNumber: string | null
  newSerialNumber: string | null
}

// ---------------------------------------------------------------------------
// Intranettet (beslutning 100)
// ---------------------------------------------------------------------------

export interface IntranetPhoto {
  id: number
  /** Ligger bag login - hentes med useAuthedBlob. */
  url: string
  caption?: string | null
}

export interface IntranetPost {
  id: number
  title: string
  content: string
  isPinned: boolean
  /** Kun til én arbejdsgruppe (beslutning 101)? Null = hele huset. */
  groupId?: number | null
  groupName?: string | null
  requiresAck: boolean
  createdBy?: UserRef | null
  createdAt: string
  updatedAt?: string | null
  readCount: number
  recipientCount: number
  hasRead: boolean
  photos: IntranetPhoto[]
}

export interface IntranetPostReads {
  read: { user: UserRef; readAt: string }[]
  missing: UserRef[]
}

export interface IntranetPostInput {
  title: string
  content: string
  isPinned: boolean
  groupId: number | null
  requiresAck: boolean
  notify?: boolean
  notifySms?: boolean
}

export type IntranetVote = 'yes' | 'no' | 'maybe'

export interface IntranetEventVote {
  user: UserRef
  vote: IntranetVote
  voteName: string
}

export interface IntranetEventDate {
  id: number
  startsAt: string
  endsAt?: string | null
  allDay: boolean
  yesCount: number
  maybeCount: number
  noCount: number
  myVote?: IntranetVote | null
  votes: IntranetEventVote[]
}

export interface IntranetEvent {
  id: number
  title: string
  description?: string | null
  kind: string
  kindName: string
  kindIcon: string
  location?: string | null
  audienceAll: boolean
  invitees: UserRef[]
  groups: IntranetGroupRef[]
  responseDeadline?: string | null
  chosenDateId?: number | null
  publishAt?: string | null
  publishedAt?: string | null
  notifyPush: boolean
  notifySms: boolean
  reminderDaysBefore?: number | null
  isCancelled: boolean
  createdBy?: UserRef | null
  createdAt: string
  canEdit: boolean
  isInvited: boolean
  myGuestCount: number
  myNote?: string | null
  responseCount: number
  recipientCount: number
  dates: IntranetEventDate[]
}

export interface IntranetEventInput {
  title: string
  description: string | null
  kind: string
  location: string | null
  audienceAll: boolean
  inviteeIds: number[]
  groupIds: number[]
  dates: { startsAt: string; endsAt: string | null; allDay: boolean }[]
  responseDeadline: string | null
  publishAt: string | null
  notifyPush: boolean
  notifySms: boolean
  reminderDaysBefore: number | null
}

export interface IntranetEventKind {
  code: string
  name: string
  icon: string
}

export interface IntranetLink {
  id: number
  title: string
  url: string
  description?: string | null
  icon?: string | null
  sortOrder: number
  isActive: boolean
}

export interface IntranetLinkInput {
  title: string
  url: string
  description: string | null
  icon: string | null
  sortOrder: number
  isActive: boolean
}

// --- Arbejdsgrupper (beslutning 101), interne opgaver (102) og tallet i menuen (103) ---

export interface IntranetGroupRef {
  id: number
  name: string
  icon?: string | null
  memberCount: number
}

export interface IntranetGroup {
  id: number
  name: string
  description?: string | null
  icon?: string | null
  leader?: UserRef | null
  isActive: boolean
  isMember: boolean
  memberCount: number
  openTaskCount: number
  members: UserRef[]
  /** Tager imod nærved-hændelser (beslutning 113). */
  receivesIncidents: boolean
  /** Pakker varer til bilerne (beslutning 127). */
  packsVanOrders: boolean
}

export interface IntranetGroupInput {
  name: string
  description: string | null
  icon: string | null
  leaderUserId: number | null
  isActive: boolean
  receivesIncidents: boolean
  packsVanOrders: boolean
  memberIds: number[]
}

export interface IntranetTask {
  id: number
  title: string
  description?: string | null
  assignee?: UserRef | null
  groupId?: number | null
  groupName?: string | null
  dueDate?: string | null
  isDone: boolean
  doneAt?: string | null
  doneBy?: UserRef | null
  createdBy?: UserRef | null
  createdAt: string
  canClose: boolean
  /** Privat (beslutning 129) - kun den ansvarlige og den, der oprettede den, ser den. */
  isPrivate?: boolean
}

export interface IntranetTaskInput {
  title: string
  description: string | null
  assigneeUserId: number | null
  groupId: number | null
  dueDate: string | null
}

/** Tallet ved "Intranet" i menuen - hvor meget der er nyt siden sidst. */
export interface IntranetUnseen {
  posts: number
  mustRead: number
  events: number
  tasks: number
  /** Dokumenter, der kraever en kvittering, man ikke har givet (beslutning 104). */
  documents: number
  total: number
  /** Nye nærved-hændelser - kun for dem, der tager imod (beslutning 113). */
  incidents?: number
  /** Ubesvarede idéer - kun for ledelsen (beslutning 115). */
  ideas?: number
  /** Åbne afstemninger, man ikke har stemt i (beslutning 116). */
  polls?: number
  /** Ros til en selv siden sidst (beslutning 121). */
  kudos?: number
  /** Ugens tip, man ikke har kvitteret for. */
  tips?: number
  /** Nye maaltider med aaben tilmelding (beslutning 124). */
  meals?: number
  /** En aaben APV-runde, man ikke har svaret paa (beslutning 125). */
  apv?: number
  /** Referater fra personalemøder, man ikke har kvitteret for (beslutning 128). */
  meetings?: number
  /** MUS-samtaler, hvor man selv skal forberede sig eller godkende (beslutning 129). */
  mus?: number
}

// --- Velkommen til nye (beslutning 118) ---

export interface WelcomeItem {
  id: number
  title: string
  audience: 'office' | 'newcomer'
  dueDaysAfterStart: number
  sortOrder: number
  isActive: boolean
  /** Medarbejder, lærling eller begge (beslutning 118). */
  track: WelcomeTrack
}

export type WelcomeTrack = 'both' | 'employee' | 'apprentice'

export interface WelcomeStatus {
  userId: number
  welcomedAt: string
}

// --- Fødselsdage og jubilæer (beslutning 117) ---

export interface Celebration {
  user: UserRef
  kind: 'birthday' | 'anniversary'
  /** yyyy-MM-dd */
  date: string
  /** Antal år ved et jubilæum. Null ved en fødselsdag - året gemmes ikke. */
  years?: number | null
}

export interface MyBirthday {
  month?: number | null
  day?: number | null
  show: boolean
}

export interface HireDate {
  userId: number
  /** yyyy-MM-dd */
  hireDate?: string | null
}

// --- Hurtige afstemninger (beslutning 116) ---

export interface IntranetPoll {
  id: number
  question: string
  allowMultiple: boolean
  isSecret: boolean
  closesAt?: string | null
  isOpen: boolean
  groupId?: number | null
  groupName?: string | null
  createdBy?: UserRef | null
  createdAt: string
  /** `voters` er altid tom, når afstemningen er hemmelig. */
  options: { id: number; text: string; votes: number; voters: UserRef[] }[]
  voterCount: number
  myOptionIds: number[]
  canVote: boolean
  canManage: boolean
}

// --- Idéboksen (beslutning 115) ---

export type IntranetIdeaStatus = 'new' | 'considering' | 'accepted' | 'done' | 'declined'

export interface IntranetIdea {
  id: number
  title: string
  description: string
  isAnonymous: boolean
  /** Altid null, når idéen er anonym. */
  createdBy?: UserRef | null
  status: IntranetIdeaStatus
  statusName: string
  answer?: string | null
  answeredBy?: UserRef | null
  answeredAt?: string | null
  createdAt: string
  isMine: boolean
  canAnswer: boolean
  canDelete: boolean
}

// --- Kvitteringer (beslutning 114) ---

/** Hvordan kvitteringen er betalt (beslutning 148) - afgør mappen i SharePoint. */
export type ReceiptPaymentMethod = 'employee' | 'company_card'

export interface Receipt {
  id: number
  orderNumber: string
  status: 'pending' | 'approved' | 'rejected'
  statusName: string
  user?: UserRef | null
  createdAt: string
  decidedBy?: UserRef | null
  decidedAt?: string | null
  rejectReason?: string | null
  /** Billedet bag login. */
  imageUrl: string
  /** Filen i SharePoint - kun for kontoret, og kun når den er godkendt. */
  webUrl?: string | null
  canDelete: boolean
  /** Godkende: kontoret, når den venter eller er afvist (beslutning 132). Afvise kun en ventende. */
  canDecide: boolean
  /** Kontoret altid; den, der sendte den, indtil den er godkendt (beslutning 132). */
  canEditOrderNumber: boolean
  /** Betalingsmåden (beslutning 148). Null på kvitteringer fra før - skal vælges, før den kan godkendes. */
  paymentMethod?: ReceiptPaymentMethod | null
  /** "Medarbejderudlæg", "Betalt med firmakort" eller "Ikke valgt". */
  paymentMethodText: string
  /** Er det gemte en PDF (godkendt fra beslutning 139)? Så vises den ikke som et billede. */
  isPdf: boolean
  /** Hver hentning og udskrift, nyeste først (beslutning 133). */
  exports: ReceiptExport[]
  /** Sager med samme ordrenummer. */
  projects: { id: number; name: string }[]
}

export interface ReceiptExport {
  /** `mail` = sendt videre til Ordrestyring (beslutning 136). */
  kind: 'download' | 'print' | 'mail'
  user?: UserRef | null
  createdAt: string
}

/** Nøglen til Ordrestyrings API (beslutning 137) - nøglen selv kommer aldrig med. */
export interface OrdrestyringSettings {
  hasApiKey: boolean
  checkedAt?: string | null
  checkOk: boolean
  checkMessage?: string | null
}

/** Svaret efter "Send til Ordrestyring" (beslutning 136). */
export interface ReceiptMailResult {
  to: string
  sent: number
  failed: { id: number; orderNumber: string; error: string }[]
}

export interface ReceiptMeta {
  canDecide: boolean
  pendingCount: number
}

// --- Nærved-hændelser (beslutning 113) ---

export type IntranetIncidentKind = 'near_miss' | 'unsafe_condition' | 'accident'
export type IntranetIncidentStatus = 'new' | 'investigating' | 'closed'

export interface IntranetIncident {
  id: number
  kind: IntranetIncidentKind
  kindName: string
  occurredAt: string
  location: string
  description: string
  projectId?: number | null
  projectName?: string | null
  isAnonymous: boolean
  /** Altid null, når hændelsen er anonym. */
  reportedBy?: UserRef | null
  status: IntranetIncidentStatus
  statusName: string
  followUp?: string | null
  handledBy?: UserRef | null
  closedAt?: string | null
  createdAt: string
  photos: { id: number; url: string }[]
  canFollowUp: boolean
}

export interface IntranetIncidentMeta {
  canHandle: boolean
  /** Nej = hændelserne går til kontoret og administratorerne. */
  hasReceivingGroup: boolean
  openCount: number
}

// --- Personalehaandbog og dokumenter (beslutning 104) ---

export interface IntranetDocument {
  id: number
  title: string
  description?: string | null
  category?: string | null
  groupId?: number | null
  groupName?: string | null
  /** Linket, hvis dokumentet er et link. Null = der er en fil. */
  url?: string | null
  /** Stien til filen bag login. Null = dokumentet er et link. */
  fileUrl?: string | null
  fileName?: string | null
  contentType?: string | null
  sizeBytes?: number | null
  /** Adressen i SharePoint - til den, der skal rette filen der. */
  webUrl?: string | null
  requiresAck: boolean
  sortOrder: number
  isActive: boolean
  updatedBy?: UserRef | null
  updatedAt: string
  readCount: number
  recipientCount: number
  hasRead: boolean
}

export interface IntranetDocumentInput {
  title: string
  description: string | null
  category: string | null
  groupId: number | null
  url: string | null
  requiresAck: boolean
  sortOrder: number
  isActive: boolean
}

/** En model under en enhedstype (beslutning 105). */
export interface DeviceModelOption {
  id: number
  deviceTypeId: number
  name: string
  manufacturer?: string | null
  isActive: boolean
  /** Antal enheder med modellen. */
  deviceCount: number
}

/** En enhedstype på den faste liste (beslutning 105). Enheden gemmer navnet som tekst. */
export interface DeviceTypeOption {
  id: number
  name: string
  sortOrder: number
  isActive: boolean
  deviceCount: number
  models: DeviceModelOption[]
}

// --- Vagtplan og tilkaldevagt (beslutning 120) ---

/** Én uge. Vagten løber fra mandag kl. 7 dansk tid (startsAt) til næste mandag kl. 7 (endsAt). */
export interface OnCallWeek {
  weekStart: string
  weekNumber: number
  startsAt: string
  endsAt: string
  user?: UserRef | null
  note?: string | null
  /** Fravær i ugen o.l. - spærrer ikke. */
  warnings: string[]
}

export interface OnCallNow {
  current?: OnCallWeek | null
  next?: OnCallWeek | null
}

export type OnCallSwapStatus = 'pending' | 'accepted' | 'declined' | 'cancelled'

export interface OnCallSwap {
  id: number
  weekStart: string
  weekNumber: number
  otherWeekStart?: string | null
  otherWeekNumber?: number | null
  requester: UserRef
  target: UserRef
  message?: string | null
  status: OnCallSwapStatus
  createdAt: string
}

export interface OnCallPlan {
  weeks: OnCallWeek[]
  rotation: UserRef[]
  swaps: OnCallSwap[]
}

// --- Ros, køb/salg og ugens faglige tip (beslutning 121) ---

export interface IntranetKudos {
  id: number
  message: string
  from?: UserRef | null
  recipients: UserRef[]
  createdAt: string
  canDelete: boolean
}

export interface IntranetKudosMonth {
  /** yyyy-MM */
  month: string
  items: IntranetKudos[]
  top: { user: UserRef; count: number }[]
}

export type IntranetMarketKind = 'sell' | 'wanted' | 'free' | 'lend'

export interface IntranetMarketItem {
  id: number
  kind: IntranetMarketKind
  kindName: string
  title: string
  description?: string | null
  price?: string | null
  user: UserRef
  createdAt: string
  expiresAt: string
  closedAt?: string | null
  isOpen: boolean
  isMine: boolean
  canManage: boolean
  photoUrls: string[]
}

export interface IntranetTip {
  id: number
  title: string
  body: string
  photoUrl?: string | null
  createdBy?: UserRef | null
  createdAt: string
  updatedAt?: string | null
  isThisWeek: boolean
  isRead: boolean
  readCount: number
}

// --- Firmabiler (beslutning 123) ---

export type VehicleDueLevel = 'ok' | 'soon' | 'overdue'

export interface VehicleDue {
  kind: 'inspection' | 'service' | 'service_km' | 'tyres' | 'insurance' | 'leasing'
  label: string
  date?: string | null
  daysUntil?: number | null
  kmLeft?: number | null
  level: VehicleDueLevel
}

export interface Vehicle {
  id: number
  registrationNumber: string
  displayPlate: string
  name?: string | null
  make?: string | null
  model?: string | null
  year?: number | null
  driver?: UserRef | null
  stockLocationId?: number | null
  stockLocationName?: string | null
  nextInspectionOn?: string | null
  nextServiceOn?: string | null
  nextServiceKm?: number | null
  currentKm?: number | null
  kmUpdatedAt?: string | null
  tyreSeason?: 'summer' | 'winter' | 'all_season' | null
  insuranceExpiresOn?: string | null
  leasingExpiresOn?: string | null
  leasingCompany?: string | null
  notes?: string | null
  isActive: boolean
  isMine: boolean
  canEdit: boolean
  openDamages: number
  due: VehicleDue[]
  /** Billedet af bilen med versionsnummer (beslutning 135). */
  photoUrl?: string | null
  /** Nummerpladens farve (beslutning 135). */
  plateColor: 'yellow' | 'white' | 'split'
  /** Ekstranøglens nummer i nøgleskabet. */
  spareKeyNumber?: string | null
}

export type VehicleDamageStatus = 'new' | 'ordered' | 'fixed'

export interface VehicleDamage {
  id: number
  vehicleId: number
  description: string
  location?: string | null
  occurredOn?: string | null
  status: VehicleDamageStatus
  statusName: string
  followUp?: string | null
  reportedBy?: UserRef | null
  reportedAt: string
  handledBy?: UserRef | null
  handledAt?: string | null
  photoUrls: string[]
  /** Tilkaldt (beslutning 135). */
  roadsideAssistanceCalled: boolean
  policeCalled: boolean
  ambulanceCalled: boolean
  /** HC-Auto er tilkaldt. */
  hcAutoCalled: boolean
  /** Var der en modpart? Selve oplysningerne kommer kun til kontoret og den, der meldte skaden. */
  hasCounterparty: boolean
  counterparty?: VehicleCounterparty | null
}

export interface VehicleCounterparty {
  plate?: string | null
  name?: string | null
  phone?: string | null
  insurance?: string | null
  photoUrls: string[]
}

export interface VehicleDetail {
  vehicle: Vehicle
  damages: VehicleDamage[]
}

export interface VehicleSaveInput {
  registrationNumber: string
  plateColor: 'yellow' | 'white' | 'split'
  spareKeyNumber?: string | null
  name?: string | null
  make?: string | null
  model?: string | null
  year?: number | null
  driverUserId?: number | null
  stockLocationId?: number | null
  nextInspectionOn?: string | null
  nextServiceOn?: string | null
  nextServiceKm?: number | null
  currentKm?: number | null
  tyreSeason?: 'summer' | 'winter' | 'all_season' | null
  insuranceExpiresOn?: string | null
  leasingExpiresOn?: string | null
  leasingCompany?: string | null
  notes?: string | null
  isActive: boolean
}

// --- Fælles måltider (beslutning 124) ---

export interface IntranetMeal {
  id: number
  title: string
  description?: string | null
  servedAt: string
  closesAt: string
  isOpen: boolean
  isCancelled: boolean
  choreText?: string | null
  choreUser?: UserRef | null
  count: number
  iAmComing: boolean
  myNeeds?: string | null
  /** Kun til kontoret. */
  signups?: { user: UserRef; needs?: string | null }[] | null
  summary?: string | null
  createdBy?: UserRef | null
}

// --- APV og kemikalier (beslutning 125) ---

export type ApvAnswerValue = 'ok' | 'problem' | 'na'

export interface ApvQuestion {
  id: number
  area: string
  text: string
  /** Kun til kontoret. */
  result?: { ok: number; problem: number; notRelevant: number; comments: string[] } | null
}

export interface ApvAction {
  id: number
  questionId?: number | null
  taskId: number
  title: string
  description?: string | null
  assignee?: UserRef | null
  dueDate?: string | null
  isDone: boolean
  doneAt?: string | null
}

export interface ApvRound {
  id: number
  title: string
  startedAt: string
  closesAt: string
  completedAt?: string | null
  isOpen: boolean
  iAnswered: boolean
  responses: number
  activeUsers?: number | null
  questions: ApvQuestion[]
  actions: ApvAction[]
}

export interface ApvOverview {
  current?: ApvRound | null
  history: { id: number; title: string; startedAt: string; completedAt?: string | null; responses: number }[]
  nextDue?: string | null
  defaultQuestions?: { area: string; text: string }[] | null
}

export interface Chemical {
  id: number
  name: string
  supplier?: string | null
  productNumber?: string | null
  usedFor?: string | null
  location?: string | null
  hazards?: string | null
  hasSds: boolean
  sdsFileName?: string | null
  sdsDate?: string | null
  sdsIsOld: boolean
  hasApb: boolean
  apbFileName?: string | null
  isActive: boolean
  updatedAt: string
}

export interface ChemicalSaveInput {
  name: string
  supplier?: string | null
  productNumber?: string | null
  usedFor?: string | null
  location?: string | null
  hazards?: string | null
  sdsDate?: string | null
  isActive: boolean
}


// ---------- Varer til bilen (beslutning 127) ----------

export type VanOrderStatus = 'sent' | 'packed' | 'picked_up' | 'cancelled'

export interface VanOrderPlace {
  id: number
  name: string
  code: string
}

export interface VanOrderLine {
  id: number
  /** Null = fri tekst. */
  itemId?: number | null
  itemCode?: string | null
  name: string
  unit?: string | null
  quantity: number
  /** Null = ikke pakket endnu. */
  packedQuantity?: number | null
}

export interface VanOrder {
  id: number
  van: VanOrderPlace
  from?: VanOrderPlace | null
  status: VanOrderStatus
  note?: string | null
  packNote?: string | null
  orderedBy: UserRef
  packedBy?: UserRef | null
  packedAt?: string | null
  pickedUpBy?: UserRef | null
  pickedUpAt?: string | null
  cancelledAt?: string | null
  createdAt: string
  lines: VanOrderLine[]
  canPack: boolean
  canPickUp: boolean
  canCancel: boolean
  hasRest: boolean
}

export interface VanOrderContext {
  canPack: boolean
  hasPackingGroup: boolean
  myVans: VanOrderPlace[]
  allVans: VanOrderPlace[]
  packFrom: VanOrderPlace[]
}

export interface VanOrderSuggestion {
  itemId: number
  name: string
  unit: string
  inVan: number
  minimum: number
  quantity: number
}

export interface VanOrderLineInput {
  itemId?: number | null
  text?: string | null
  quantity: number
}

export interface VanOrderInput {
  vanLocationId: number
  note?: string | null
  lines: VanOrderLineInput[]
}

export interface VanOrderPackInput {
  fromLocationId: number
  packNote?: string | null
  lines: { lineId: number; packedQuantity: number }[]
}

// ---------- Personalemøder (beslutning 128) ----------

export interface IntranetMeetingListItem {
  id: number
  title: string
  startsAt: string
  location?: string | null
  groupId?: number | null
  groupName?: string | null
  itemCount: number
  isCancelled: boolean
  minutesPublishedAt?: string | null
  /** Altid sand, når der ikke er et referat. */
  hasRead: boolean
}

export interface IntranetMeetingItem {
  id: number
  title: string
  description?: string | null
  proposedBy?: UserRef | null
  /** Referatets noter - kun når det er udgivet, eller til kontoret. */
  notes?: string | null
  canEdit: boolean
}

export interface IntranetMeetingDecision {
  id: number
  itemId?: number | null
  text: string
  assignee?: UserRef | null
  dueDate?: string | null
  taskId?: number | null
  taskIsDone: boolean
}

export interface IntranetMeeting {
  id: number
  title: string
  startsAt: string
  location?: string | null
  groupId?: number | null
  groupName?: string | null
  isCancelled: boolean
  summary?: string | null
  minutesPublishedAt?: string | null
  minutesPublishedBy?: UserRef | null
  createdBy?: UserRef | null
  items: IntranetMeetingItem[]
  decisions: IntranetMeetingDecision[]
  canManage: boolean
  canAddItem: boolean
  hasRead: boolean
  readCount: number
  participantCount: number
  notRead: UserRef[]
}

export interface IntranetMeetingInput {
  title: string
  startsAt: string
  location?: string | null
  groupId?: number | null
}

export interface IntranetMeetingMinutesInput {
  summary?: string | null
  items: { itemId: number; notes?: string | null }[]
  decisions: { itemId?: number | null; text: string; assigneeUserId?: number | null; dueDate?: string | null }[]
}

// ---------- MUS-samtaler (beslutning 129) ----------

export type MusStatus = 'planned' | 'awaiting_approval' | 'approved' | 'cancelled'
export type MusSide = 'employee' | 'leader'

/** En samtale UDEN indhold - kun hvem, hvornår og status. */
export interface MusSummary {
  id: number
  employee: UserRef
  leader: UserRef
  scheduledAt: string
  location?: string | null
  status: MusStatus
  canSchedule: boolean
}

export interface MusOverviewRow {
  employee: UserRef
  lastHeldAt?: string | null
  next?: MusSummary | null
  isDue: boolean
}

export interface MusAnswer {
  id: number
  side: MusSide
  question: string
  answer?: string | null
  canEdit: boolean
}

export interface MusAgreement {
  id: number
  text: string
  assignee?: UserRef | null
  dueDate?: string | null
  taskId?: number | null
  taskIsDone: boolean
}

/** Hele samtalen - kun til medarbejderen og lederen. */
export interface MusConversation {
  id: number
  employee: UserRef
  leader: UserRef
  scheduledAt: string
  location?: string | null
  status: MusStatus
  mySide: MusSide
  employeeReadyAt?: string | null
  leaderReadyAt?: string | null
  answers: MusAnswer[]
  minutes?: string | null
  agreements: MusAgreement[]
  minutesSharedAt?: string | null
  approvedAt?: string | null
  employeeComment?: string | null
  canSchedule: boolean
}

export interface MusQuestion {
  id: number
  text: string
  side: MusSide
  sortOrder: number
  isActive: boolean
}

// ---------- Lærlingeplan (beslutning 130) ----------

export interface ApprenticeshipListItem {
  id: number
  apprentice: UserRef
  journeyman?: UserRef | null
  education: string
  startDate: string
  expectedEndDate?: string | null
  completedAt?: string | null
  goalsTotal: number
  goalsApproved: number
  lastLogDate?: string | null
}

export interface ApprenticeGoal {
  id: number
  area: string
  text: string
  approvedAt?: string | null
  approvedBy?: UserRef | null
  comment?: string | null
}

export interface ApprenticeLogEntry {
  id: number
  date: string
  text: string
  projectId?: number | null
  projectName?: string | null
  goalId?: number | null
  goalText?: string | null
  comment?: string | null
  commentedBy?: UserRef | null
  commentedAt?: string | null
  photos: { id: number; url: string }[]
}

export interface Apprenticeship {
  id: number
  apprentice: UserRef
  journeyman?: UserRef | null
  education: string
  startDate: string
  expectedEndDate?: string | null
  completedAt?: string | null
  notes?: string | null
  goals: ApprenticeGoal[]
  log: ApprenticeLogEntry[]
  schoolPeriods: { absenceId: number; startDate: string; endDate: string; note?: string | null }[]
  isApprentice: boolean
  canSupervise: boolean
  canManage: boolean
}

export interface ApprenticeGoalTemplate {
  id: number
  area: string
  text: string
  sortOrder: number
  isActive: boolean
}

// ---------- Egenkontrol (beslutning 131) ----------

export type SelfCheckResult = 'ok' | 'not_ok' | 'na'

export interface SelfCheckSummary {
  id: number
  title: string
  createdAt: string
  createdBy?: UserRef | null
  completedAt?: string | null
  completedBy?: UserRef | null
  hasPdf: boolean
  pdfWebUrl?: string | null
  uploadError?: string | null
}

export interface ProjectSelfChecks {
  /** Skal sagen have en? Kun almindelige sager - ikke servicesager. */
  expected: boolean
  hasCompleted: boolean
  checks: SelfCheckSummary[]
}

export interface SelfCheckItem {
  id: number
  text: string
  result?: SelfCheckResult | null
  note?: string | null
}

export interface SelfCheckCircuit {
  id?: number
  name: string
  protection?: string | null
  continuityOhm?: number | null
  insulationMOhm?: number | null
  rcdRatedMa?: number | null
  rcdTripMs?: number | null
  loopImpedanceOhm?: number | null
  isOk: boolean
  note?: string | null
}

export interface SelfCheck {
  id: number
  projectId: number
  projectName: string
  title: string
  notes?: string | null
  instrumentId?: number | null
  instrumentName?: string | null
  instrumentSerialNumber?: string | null
  instrumentCalibrationValidUntil?: string | null
  items: SelfCheckItem[]
  circuits: SelfCheckCircuit[]
  createdAt: string
  createdBy?: UserRef | null
  completedAt?: string | null
  completedBy?: UserRef | null
  hasPdf: boolean
  pdfWebUrl?: string | null
  uploadError?: string | null
  /** Hvorfor den ikke kan afsluttes endnu - null = den kan. */
  whyNotCompletable?: string | null
  canEdit: boolean
  canDelete: boolean
}

export interface SelfCheckTemplateItem {
  id: number
  text: string
  sortOrder: number
  isActive: boolean
}
